import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import {
    LayoutDashboard, Users, Target, Calendar, HandCoins, PlusSquare, Send,
    Settings, User, DollarSign, BarChart2, Mail, MessageSquare,
    ChevronLeft, X, Plus, Trash2, Edit2, UploadCloud, Search, Check, FileText, Menu,
    Moon, Sun, LogOut, Repeat, FileUp, Download, Link, UserCheck, BarChartHorizontal,
    Zap, UserPlus, Ticket, Smartphone, Upload, Merge, AlertCircle, Heart, List
} from 'lucide-react';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from './AuthContext';
import LoginPage from './LoginPage';
import AdminDonationsPage from './AdminDonationsPage';
import { api } from './api';

// --- Mock Data ---
const EXAMPLE_DONORS = [
    { id: '_example1', name: 'Alice Johnson', email: 'alice.j@example.com', phone: '555-123-4567', totalDonated: 470, giftCount: 3, _isExample: true },
    { id: '_example2', name: 'Bob Williams', email: 'bob.w@example.com', phone: '555-987-6543', totalDonated: 325, giftCount: 2, _isExample: true },
];
const MOCK_DONORS = EXAMPLE_DONORS;

const MOCK_VOLUNTEERS = [
    { id: 'vol1', name: 'David Chen', phone: '555-111-2222', email: 'david.c@example.com' },
    { id: 'vol2', name: 'Emily Rodriguez', phone: '555-333-4444', email: 'emily.r@example.com' },
];

const MOCK_CAMPAIGNS = [
    { id: 'camp1', name: '2025 Summer Fundraiser', goal: 5000, raised: 3450, _isExample: true },
    { id: 'camp2', name: 'Community Outreach Program', goal: 2500, raised: 2100, _isExample: true },
];

const MOCK_EVENTS = [
    { id: 'evt1', name: 'Annual Charity Gala', date: '2025-12-12', location: 'Grand Ballroom', registered: 82, capacity: 150, raised: 12400, goal: 10000, attendees: [{id: 'user1', name: 'Alice Johnson'}, {id: 'user2', name: 'Bob Williams'}] }
];

const MOCK_PLEDGES = [
    { id: 'plg1', donorId: 'user2', donorName: 'Bob Williams', amount: 500, dueDate: '2025-09-01', status: 'Pending' },
    { id: 'plg2', donorId: 'user3', donorName: 'Charlie Brown', amount: 1000, dueDate: '2025-08-15', status: 'Fulfilled' }
];

const MOCK_TASKS = [
    { id: 1, text: 'Follow up with Alice Johnson', assigneeId: 'vol3', donorId: 'user1', completed: false },
    { id: 2, text: 'Send welcome packet to Bob Williams', assigneeId: 'vol1', donorId: 'user2', completed: true },
    { id: 3, text: 'Call Diana Prince to thank for large gift', assigneeId: 'vol3', donorId: 'user4', completed: false },
];

const MOCK_GRANTS = [
    { id: 'grt1', funder: 'Community Foundation', amount: 10000, deadline: '2025-08-30', status: 'Applied', description: 'Grant for summer outreach program.' },
    { id: 'grt2', funder: 'Arts Council', amount: 5000, deadline: '2025-07-20', status: 'Awarded', description: 'Funding for community art classes.' }
];

const MOCK_USERS_DB = {
    'admin1': { id: 'admin1', name: 'Admin User', email: 'admin@example.com', phone: '555-000-0001', role: 'Admin', org: 'Non-Profit Inc.', _isExample: true },
    'user1': { id: 'user1', name: 'Alice Johnson', email: 'alice.j@example.com', phone: '555-123-4567', role: 'Donor', org: null, _isExample: true },
};

const MOCK_NOTES = {
    'user1': [{ id: 1, date: '2025-10-26T10:00:00Z', text: 'Called to thank for recent gift.' }],
    'user2': [{ id: 1, date: '2025-10-21T15:00:00Z', text: 'Sent personal thank-you email.' }],
};

const MOCK_RECEIPTS = {
    'user1': [
        { id: 'rec1', org: 'Non-Profit Inc.', amount: 470, date: '2025-10-20', pdfUrl: '#' },
        { id: 'rec2', org: 'Local Food Bank', amount: 500, date: '2025-10-15', pdfUrl: '#' },
        { id: 'rec3', org: 'City Animal Shelter', amount: 250, date: '2025-09-01', type: 'Uploaded', fileName: 'animals_receipt_2025.pdf' }
    ],
    'user4': [
        { id: 'rec4', org: 'Non-Profit Inc.', amount: 1200, date: '2025-11-01', pdfUrl: '#' }
    ]
};

// --- Reusable Components ---

const StatCard = ({ title, value, icon }) => (
    <div className="relative bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 group overflow-hidden border border-gray-100 dark:border-slate-800">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
        <div className="flex items-center justify-between">
            <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{title}</h3>
                <p className="stat-value font-bold mt-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{value}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <div className="text-white">{icon}</div>
            </div>
        </div>
        <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-blue-100 to-transparent dark:from-blue-900 dark:to-transparent rounded-tl-full opacity-30"></div>
    </div>
);

const ProgressBar = ({ progress }) => (
    <div className="w-full bg-gray-200 rounded-full h-3 dark:bg-gray-700 overflow-hidden shadow-inner">
        <div
            className="h-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500 ease-out relative overflow-hidden"
            style={{ width: `${progress}%` }}
        >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
        </div>
    </div>
);

const Modal = ({ children, onClose }) => (
    <div className="modal-mobile-backdrop fixed inset-0 z-50 flex items-center justify-center bg-slate-950 bg-opacity-60 backdrop-blur-md">
        <div className="modal-mobile bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-6 w-full max-w-lg relative border border-gray-200 dark:border-slate-800 transform transition-all">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
            >
                <X size={20} />
            </button>
            {children}
        </div>
    </div>
);

const EmptyState = ({ message, icon: Icon }) => (
    <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl mt-4">
        <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-full mb-4">
            <Icon size={40} className="text-slate-400" />
        </div>
        <p className="text-slate-500 dark:text-slate-400 font-medium text-center">{message}</p>
    </div>
);

// --- UPDATED Confirmation Modal ---
const ConfirmationModal = ({ config, onClose }) => {
    if (!config) return null;
    const { title, message, onConfirm, confirmText = "OK" } = config;

    return (
        <Modal onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{title}</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{message}</p>
            <div className="flex justify-end mt-8 gap-3">
                <button
                    className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 hover:from-gray-200 hover:to-gray-300 dark:hover:from-gray-500 dark:hover:to-gray-600 px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
                    onClick={onClose}
                >
                    {onConfirm ? "Cancel" : "OK"}
                </button>
                {onConfirm && (
                    <button
                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                    >
                        {confirmText}
                    </button>
                )}
            </div>
        </Modal>
    );
};

const TabButton = ({ name, label, activeTab, setTab }) => (
    <button 
        className={`tab-button ${activeTab === name ? 'active' : ''}`}
        onClick={() => setTab(name)}
    >
        {label}
    </button>
);

const PaymentButton = ({ onClick, isActive, children }) => (
    <button
        type="button"
        className={`payment-button ${isActive ? 'active' : ''}`}
        onClick={onClick}
    >
        {children}
    </button>
);


// --- CRM Page Components ---

const DashboardPage = ({ donors, tasks, campaigns }) => {
    const [stats, setStats] = useState({ total_donations: 0, total_raised: 0, avg_donation: 0, recurring_count: 0 });
    useEffect(() => {
        api.getStats().then(({ stats }) => setStats(stats)).catch(() => {});
    }, []);
    const totalRaised = stats.total_raised;
    const pendingTasks = useMemo(() => tasks.filter(t => !t.completed).length, [tasks]);

    const volunteerData = [
        { name: 'David Chen', raised: 28500 },
        { name: 'Emily Rodriguez', raised: 24300 },
        { name: 'Frank White', raised: 19800 },
        { name: 'Grace Lee', raised: 15600 },
        { name: 'Henry Wilson', raised: 12400 }
    ];

    const donorTypeData = [
        { name: 'Individual', value: 68, color: '#3b82f6' },
        { name: 'Corporate', value: 22, color: '#8b5cf6' },
        { name: 'Foundation', value: 10, color: '#ec4899' }
    ];

    const retentionData = [
        { quarter: 'Q1', rate: 72 },
        { quarter: 'Q2', rate: 78 },
        { quarter: 'Q3', rate: 75 },
        { quarter: 'Q4', rate: 82 }
    ];

    const donationData = [
        { month: 'Jan', amount: 18500, donors: 45 },
        { month: 'Feb', amount: 22300, donors: 52 },
        { month: 'Mar', amount: 19800, donors: 48 },
        { month: 'Apr', amount: 25600, donors: 61 },
        { month: 'May', amount: 28900, donors: 68 },
        { month: 'Jun', amount: 32100, donors: 74 },
        { month: 'Jul', amount: 29400, donors: 70 },
        { month: 'Aug', amount: 31200, donors: 73 },
        { month: 'Sep', amount: 27800, donors: 65 },
        { month: 'Oct', amount: 26500, donors: 63 },
        { month: 'Nov', amount: 24700, donors: 58 },
        { month: 'Dec', amount: totalRaised / 10 } // Tie last month to real data
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                    <LayoutDashboard size={32} className="text-white" />
                </div>
                <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">CRM Dashboard</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Welcome back! Here's your overview</p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Total Raised" value={`$${Number(totalRaised).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}`} icon={<DollarSign size={28} />} />
                <StatCard title="Total Donations" value={stats.total_donations} icon={<List size={28} />} />
                <StatCard title="Avg Donation" value={`$${Number(stats.avg_donation).toFixed(2)}`} icon={<Target size={28} />} />
                <StatCard title="Recurring Donors" value={stats.recurring_count} icon={<Repeat size={28} />} />

                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-800 md:col-span-2 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Donations by Month</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={donationData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="month" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                            <Legend />
                            <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={3} name="Amount ($)" />
                            <Line type="monotone" dataKey="donors" stroke="#8b5cf6" strokeWidth={3} name="Donors" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-800 md:col-span-2 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Top Fundraisers</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <RechartsBarChart data={volunteerData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis type="number" stroke="#6b7280" />
                            <YAxis dataKey="name" type="category" width={120} stroke="#6b7280" />
                            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                            <Bar dataKey="raised" fill="#3b82f6" radius={[0, 8, 8, 0]} name="Amount Raised ($)" />
                        </RechartsBarChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Donor Types</h3>
                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie data={donorTypeData} cx="50%" cy="50%" labelLine={false} label={({name, value}) => `${name}: ${value}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                                {donorTypeData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Donor Retention Rate</h3>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart data={retentionData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="quarter" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" domain={[0, 100]} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                                formatter={(value) => `${value}%`}
                            />
                            <Line type="monotone" dataKey="rate" stroke="#10b981" strokeWidth={3} name="Retention Rate" dot={{ fill: '#10b981', r: 5 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

const DUPES_PAGE_SIZE = 20;

const DuplicatesModal = ({ duplicates, onMerge, onMergeAll, mergingAll, mergeProgress, onClose }) => {
    const [page, setPage] = useState(1);
    const totalPages = Math.ceil(duplicates.length / DUPES_PAGE_SIZE);
    const visible = duplicates.slice((page - 1) * DUPES_PAGE_SIZE, page * DUPES_PAGE_SIZE);

    return (
        <Modal onClose={onClose}>
            <div className="flex items-start justify-between mb-1">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Merge className="text-purple-500" />
                    Duplicate Donors Found
                </h2>
                {duplicates.length > 1 && (
                    <button
                        onClick={onMergeAll}
                        disabled={mergingAll}
                        className="ml-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 flex items-center gap-2 shrink-0"
                    >
                        <Merge size={15} />
                        {mergingAll ? `Merging… ${mergeProgress}/${duplicates.length}` : `Merge All ${duplicates.length}`}
                    </button>
                )}
            </div>
            {duplicates.length > 0 && (
                <p className="text-sm text-gray-500 mb-4">{duplicates.length} group{duplicates.length !== 1 ? 's' : ''} — matches on email or phone + name only (never name alone)</p>
            )}
            {duplicates.length === 0 ? (
                <div className="text-center py-8">
                    <Check size={48} className="mx-auto text-green-500 mb-3" />
                    <p className="text-lg font-medium text-gray-700 dark:text-gray-300">No duplicates found!</p>
                    <p className="text-sm text-gray-500 mt-2">Your donor list is clean.</p>
                </div>
            ) : (
                <>
                    <div className="space-y-4 max-h-[460px] overflow-y-auto">
                        {visible.map((group, i) => {
                            const groupIndex = (page - 1) * DUPES_PAGE_SIZE + i;
                            return (
                                <div key={groupIndex} className="border border-orange-300 dark:border-orange-700 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-semibold text-orange-800 dark:text-orange-300 flex items-center gap-2">
                                            <AlertCircle size={18} />
                                            Group {groupIndex + 1}
                                        </h3>
                                        <button
                                            onClick={() => onMerge(group)}
                                            className="px-3 py-1 bg-purple-500 text-white text-sm rounded-md hover:bg-purple-600 flex items-center gap-1"
                                        >
                                            <Merge size={14} />
                                            Merge All
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {group.map(donor => (
                                            <div key={donor.id} className="bg-white dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                                                <p className="font-medium">{donor.name || '—'}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{donor.email || '—'}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{donor.phone || '—'}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t dark:border-slate-700">
                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                            <div className="flex gap-2">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    className="px-3 py-1 text-sm rounded-md bg-gray-100 dark:bg-slate-700 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-600">Prev</button>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                    className="px-3 py-1 text-sm rounded-md bg-gray-100 dark:bg-slate-700 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-600">Next</button>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className="flex justify-end mt-4">
                <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500">Close</button>
            </div>
        </Modal>
    );
};

const DonorManagementPage = ({ donors, notes, setNotes, setDonors, receipts, pledges, setModal }) => {
    const [filter, setFilter] = useState('');
    const [searchResults, setSearchResults] = useState(null); // null = use full donors list
    const [searching, setSearching] = useState(false);
    const [giftCountFilter, setGiftCountFilter] = useState('any');
    const [totalDonatedFilter, setTotalDonatedFilter] = useState('any');
    const [lapsedFilter, setLapsedFilter] = useState('any');
    const [donorPage, setDonorPage] = useState(1);
    const DONOR_PAGE_SIZE = 50;
    const [viewingDonor, setViewingDonor] = useState(null);
    const [editingDonor, setEditingDonor] = useState(false);
    const [editFields, setEditFields] = useState({ name: '', email: '', phone: '' });
    const [editSaving, setEditSaving] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [showDuplicates, setShowDuplicates] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [checkingDuplicates, setCheckingDuplicates] = useState(false);
    const [selectedForMerge, setSelectedForMerge] = useState([]);
    const [thankedIds, setThankedIds] = useState(new Set());
    const [emailCopied, setEmailCopied] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);

    // Server-side search: fires 400ms after the user stops typing
    useEffect(() => {
        if (!filter.trim()) { setSearchResults(null); return; }
        setSearching(true);
        const timer = setTimeout(() => {
            api.getDonors(filter.trim())
                .then(({ donors: results }) => setSearchResults(results.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 }))))
                .catch(() => setSearchResults([]))
                .finally(() => setSearching(false));
        }, 400);
        return () => clearTimeout(timer);
    }, [filter]);

    const activeDonors = searchResults !== null ? searchResults : donors;

    const donorDataWithLastGift = useMemo(() => {
        const now = new Date();
        return activeDonors.map(donor => {
            const lastGiftDate = donor.lastGiftDate || null;
            let lapsedStatus = 'never';
            if (lastGiftDate) {
                const monthsAgo = (now - new Date(lastGiftDate)) / (1000 * 60 * 60 * 24 * 30);
                if (monthsAgo < 6) lapsedStatus = 'active';
                else if (monthsAgo < 12) lapsedStatus = 'lapsing';
                else lapsedStatus = 'lapsed';
            }
            return { ...donor, lastGiftDate, lapsedStatus };
        });
    }, [activeDonors]);

    const filteredDonors = useMemo(() =>
        donorDataWithLastGift.filter(d => {
            const giftCountPass = giftCountFilter === 'any' ||
                (giftCountFilter === '1-5' && d.giftCount >= 1 && d.giftCount <= 5) ||
                (giftCountFilter === '6+' && d.giftCount >= 6);

            const totalDonatedPass = totalDonatedFilter === 'any' ||
                (totalDonatedFilter === '1-100' && d.totalDonated >= 1 && d.totalDonated <= 100) ||
                (totalDonatedFilter === '101-500' && d.totalDonated >= 101 && d.totalDonated <= 500) ||
                (totalDonatedFilter === '501+' && d.totalDonated >= 501);

            const lapsedPass = lapsedFilter === 'any' || d.lapsedStatus === lapsedFilter;

            return giftCountPass && totalDonatedPass && lapsedPass;
        }).sort((a, b) => (b.lastGiftDate || '') > (a.lastGiftDate || '') ? 1 : -1),
        [donorDataWithLastGift, giftCountFilter, totalDonatedFilter, lapsedFilter]
    );

    // Reset to page 1 when filters change
    useEffect(() => { setDonorPage(1); }, [filter, giftCountFilter, totalDonatedFilter, lapsedFilter]);

    const copyEmails = () => {
        const emails = filteredDonors.map(d => d.email).filter(Boolean).join(', ');
        navigator.clipboard.writeText(emails).then(() => {
            setEmailCopied(true);
            setTimeout(() => setEmailCopied(false), 2500);
        });
    };

    const exportEmails = () => {
        const emails = filteredDonors.map(d => d.email).filter(Boolean).join('\n');
        const blob = new Blob([emails], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `emails-${new Date().toISOString().slice(0,10)}.txt`; a.click();
        URL.revokeObjectURL(url);
    };

    const exportPhones = () => {
        const phones = filteredDonors.map(d => d.phone).filter(Boolean).join('\n');
        const blob = new Blob([phones], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `phones-${new Date().toISOString().slice(0,10)}.txt`; a.click();
        URL.revokeObjectURL(url);
    };

    const exportDonorsCSV = () => {
        const headers = ['Name', 'Email', 'Phone', 'Total Donated', 'Gift Count', 'Last Gift', 'Status'];
        const esc = v => v == null ? '' : `"${String(v).replace(/"/g, '""')}"`;
        const rows = filteredDonors.map(d => [
            d.name, d.email, d.phone || '',
            d.totalDonated, d.giftCount,
            d.lastGiftDate ? new Date(d.lastGiftDate).toLocaleDateString() : 'Never',
            d.lapsedStatus
        ].map(esc).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `donors-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const toggleThanked = (id) => {
        setThankedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleAddNote = (e) => {
        e.preventDefault();
        if (!noteText.trim() || !viewingDonor) return;

        const newNote = {
            id: Date.now(),
            date: new Date().toISOString(),
            text: noteText
        };

        setNotes(prevNotes => ({
            ...prevNotes,
            [viewingDonor.id]: [...(prevNotes[viewingDonor.id] || []), newNote]
        }));
        setNoteText('');
    };

    const exportToVCard = (donor) => {
        const vCardData = [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${donor.name}`,
            donor.email ? `EMAIL:${donor.email}` : null,
            donor.phone ? `TEL:${donor.phone}` : null,
            'END:VCARD'
        ].filter(Boolean).join('\n');

        const blob = new Blob([vCardData], { type: 'text/vcard' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${donor.name.replace(/\s+/g, '_')}.vcf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportBulkVCards = () => {
        const eligible = donors.filter(d => (d.name || '').trim() && (d.phone || '').trim());
        if (eligible.length === 0) { alert('No donors with both a name and phone number found.'); return; }
        const vCardData = eligible.map(d => [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${d.name}`,
            d.email ? `EMAIL:${d.email}` : null,
            `TEL:${d.phone}`,
            'END:VCARD'
        ].filter(Boolean).join('\n')).join('\n');
        const blob = new Blob([vCardData], { type: 'text/vcard' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `donors-contacts-${new Date().toISOString().slice(0,10)}.vcf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImportCSV = async () => {
        const file = importFile;
        if (!file) return;
        setImporting(true);

        // Parse a CSV line respecting quoted fields
        const parseCSVLine = (line) => {
            const result = [];
            let cur = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (ch === '"') { inQuotes = !inQuotes; }
                else if (ch === ',' && !inQuotes) { result.push(cur.trim()); cur = ''; }
                else { cur += ch; }
            }
            result.push(cur.trim());
            return result;
        };

        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const text = event.target.result;
            const lines = text.split('\n');
            const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ' ').trim());

            const getCol = (values, ...names) => {
                for (const n of names) {
                    const idx = headers.indexOf(n);
                    if (idx !== -1 && values[idx]) return values[idx].trim();
                }
                return '';
            };

            const toImport = [];
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;
                const values = parseCSVLine(lines[i]);

                // Support both "name" and "first name" + "last name"
                let name = getCol(values, 'name', 'full name');
                if (!name) {
                    const first = getCol(values, 'first name', 'firstname');
                    const last = getCol(values, 'last name', 'lastname');
                    name = [first, last].filter(Boolean).join(' ');
                }

                const email = getCol(values, 'email', 'email address');
                const phone = getCol(values, 'phone', 'phone number', 'tel');
                if (!name && !email && !phone) continue; // skip fully blank rows
                const amountRaw = getCol(values, 'amount', 'total', 'totaldonated').replace(/[$,]/g, '');
                const amount = parseFloat(amountRaw) || 0;
                const date = getCol(values, 'date');
                const member = getCol(values, 'member #', 'member#', 'member', 'volunteer', 'vol #', 'vol#');

                toImport.push({ name, email, phone, amount, date, member });
            }

            if (toImport.length === 0) {
                alert('No valid rows found in CSV.');
                return;
            }

            // Send all donors+donations in one request
            const result = await api.bulkImportDonations(toImport);
            // Reload from DB so the list is accurate and persists on refresh
            const { donors: apiDonors } = await api.getDonors();
            setDonors(apiDonors.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 })));
            setShowImport(false);
            setImportFile(null);
            const parts = [];
            if (result.blank > 0) parts.push(`${result.blank} rows had no contact info`);
            const dupDonors = (toImport.length - result.blank) - result.savedDonors;
            const dupDonations = (toImport.length - result.blank) - result.savedDonations;
            if (dupDonors > 0) parts.push(`${dupDonors} donors already existed (not duplicated)`);
            if (dupDonations > 0) parts.push(`${dupDonations} donation records already existed (not duplicated)`);
            alert(`Import complete!\n\n${result.savedDonors} new donors added\n${result.savedDonations} new donation records added${parts.length > 0 ? '\n\nSkipped: ' + parts.join(', ') + '.' : ''}`);
          } catch (err) {
            alert('Import failed: ' + err.message);
          } finally {
            setImporting(false);
          }
        };
        reader.readAsText(file);
    };

    const findDuplicates = () => {
        setCheckingDuplicates(true);
        // Defer to next tick so the loading state renders before the work starts
        setTimeout(() => {
            const emailMap = new Map();
            const phoneMap = new Map();

            donors.forEach(d => {
                const email = d.email ? d.email.toLowerCase().trim() : '';
                if (email) {
                    if (!emailMap.has(email)) emailMap.set(email, []);
                    emailMap.get(email).push(d);
                }
                const phone = d.phone ? d.phone.replace(/\D/g, '') : '';
                if (phone) {
                    if (!phoneMap.has(phone)) phoneMap.set(phone, []);
                    phoneMap.get(phone).push(d);
                }
            });

            const processed = new Set();
            const dupeGroups = [];

            donors.forEach(donor => {
                if (processed.has(donor.id)) return;

                const groupSet = new Set([donor.id]);
                const group = [donor];

                const email = donor.email ? donor.email.toLowerCase().trim() : '';
                if (email) {
                    (emailMap.get(email) || []).forEach(other => {
                        if (!groupSet.has(other.id)) { groupSet.add(other.id); group.push(other); }
                    });
                }

                const phone = donor.phone ? donor.phone.replace(/\D/g, '') : '';
                const donorName = donor.name ? donor.name.toLowerCase().trim() : '';
                if (phone && donorName) {
                    (phoneMap.get(phone) || []).forEach(other => {
                        if (!groupSet.has(other.id)) {
                            const otherName = other.name ? other.name.toLowerCase().trim() : '';
                            if (otherName && otherName === donorName) {
                                groupSet.add(other.id); group.push(other);
                            }
                        }
                    });
                }

                if (group.length > 1) {
                    group.forEach(d => processed.add(d.id));
                    dupeGroups.push(group);
                }
            });

            setDuplicates(dupeGroups);
            setCheckingDuplicates(false);
            setShowDuplicates(true);
        }, 0);
    };

    // Pick the best available data across a group of duplicate donors
    const buildMergePayload = (group) => {
        const primary = group[0];
        // Longest non-empty name wins (avoids blank overwriting a real name)
        const bestName = group.reduce((best, d) => {
            const n = (d.name || '').trim();
            return n.length > best.length ? n : best;
        }, '');
        const bestEmail = group.find(d => d.email && d.email.trim())?.email?.trim() || '';
        const bestPhone = group.find(d => d.phone && d.phone.trim())?.phone?.trim() || '';
        return {
            keepId: primary.id,
            deleteIds: group.slice(1).map(d => d.id),
            name: bestName || null,
            email: bestEmail || null,
            phone: bestPhone || null,
        };
    };

    const mergeDonors = async (group) => {
        if (group.length < 2) return;
        const payload = buildMergePayload(group);
        try {
            await api.mergeBulkDonors([payload]);
            const merged = {
                ...group[0],
                name: payload.name, email: payload.email, phone: payload.phone,
                totalDonated: group.reduce((s, d) => s + (d.totalDonated || 0), 0),
                giftCount: group.reduce((s, d) => s + (d.giftCount || 0), 0),
            };
            const idsToRemove = group.map(d => d.id);
            setDonors(prev => [...prev.filter(d => !idsToRemove.includes(d.id)), merged]);
            setDuplicates(prev => prev.filter(g => !g.some(d => idsToRemove.includes(d.id))));
        } catch (err) {
            alert('Merge failed: ' + err.message);
        }
    };

    const [mergingAll, setMergingAll] = useState(false);
    const [mergeProgress, setMergeProgress] = useState(0);

    const mergeAllDuplicates = async () => {
        if (!duplicates.length) return;
        setMergingAll(true);
        setMergeProgress(0);
        const groups = duplicates.map(buildMergePayload);
        const CHUNK = 50;
        try {
            for (let i = 0; i < groups.length; i += CHUNK) {
                await api.mergeBulkDonors(groups.slice(i, i + CHUNK));
                setMergeProgress(Math.min(i + CHUNK, groups.length));
            }
            const { donors: apiDonors } = await api.getDonors();
            setDonors(apiDonors.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 })));
            setDuplicates([]);
            setShowDuplicates(false);
        } catch (err) {
            alert('Merge all failed: ' + err.message);
        } finally {
            setMergingAll(false);
            setMergeProgress(0);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold">Donor Management</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{filteredDonors.length} donor{filteredDonors.length !== 1 ? 's' : ''} shown</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Export dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowExportMenu(v => !v)}
                            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 shadow-md text-sm font-medium"
                        >
                            <Download size={16} />
                            Export
                            <ChevronLeft size={14} className={`transition-transform ${showExportMenu ? 'rotate-90' : '-rotate-90'}`} />
                        </button>
                        {showExportMenu && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700 z-20 overflow-hidden">
                                    <button onClick={() => { copyEmails(); setShowExportMenu(false); }}
                                        className={`w-full text-left px-4 py-3 text-sm flex items-center gap-3 transition-colors ${emailCopied ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
                                        {emailCopied ? <Check size={16} className="text-green-500" /> : <Mail size={16} className="text-blue-500" />}
                                        {emailCopied ? 'Copied!' : 'Copy Emails'}
                                    </button>
                                    <button onClick={() => { exportEmails(); setShowExportMenu(false); }}
                                        className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                        <Download size={16} className="text-blue-400" />
                                        Download Emails (.txt)
                                    </button>
                                    <button onClick={() => { exportPhones(); setShowExportMenu(false); }}
                                        className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                        <Download size={16} className="text-indigo-500" />
                                        Download Phones (.txt)
                                    </button>
                                    <button onClick={() => { exportDonorsCSV(); setShowExportMenu(false); }}
                                        className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                        <Download size={16} className="text-emerald-500" />
                                        Full CSV Export
                                    </button>
                                    <button onClick={() => { exportBulkVCards(); setShowExportMenu(false); }}
                                        className="w-full text-left px-4 py-3 text-sm flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-t dark:border-slate-700">
                                        <UserPlus size={16} className="text-purple-500" />
                                        Export Contacts (.vcf)
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    <button
                        onClick={() => setShowImport(true)}
                        className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center gap-2 shadow-sm text-sm font-medium"
                    >
                        <Upload size={16} />
                        Import
                    </button>
                    <button
                        onClick={findDuplicates}
                        disabled={checkingDuplicates}
                        className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center gap-2 shadow-sm text-sm font-medium disabled:opacity-60"
                    >
                        <Merge size={16} />
                        <span className="hidden sm:inline">{checkingDuplicates ? 'Checking...' : 'Duplicates'}</span>
                        <span className="sm:hidden">{checkingDuplicates ? '…' : 'Dupes'}</span>
                    </button>
                </div>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow-md border dark:border-slate-800">
                <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name, email, or phone..."
                            className="p-2 pl-10 pr-8 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        {searching && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">...</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <select
                            className="p-2 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 text-sm"
                            value={lapsedFilter}
                            onChange={(e) => setLapsedFilter(e.target.value)}
                        >
                            <option value="any">All Donors</option>
                            <option value="active">Active (gave &lt;6mo)</option>
                            <option value="lapsing">Lapsing (6–12mo)</option>
                            <option value="lapsed">Lapsed (12mo+)</option>
                            <option value="never">Never Gave</option>
                        </select>
                        <select
                            className="p-2 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 text-sm"
                            value={giftCountFilter}
                            onChange={(e) => setGiftCountFilter(e.target.value)}
                        >
                            <option value="any">Any Gift Count</option>
                            <option value="1-5">1-5 Gifts</option>
                            <option value="6+">6+ Gifts</option>
                        </select>
                        <select
                            className="p-2 border rounded-md bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 text-sm"
                            value={totalDonatedFilter}
                            onChange={(e) => setTotalDonatedFilter(e.target.value)}
                        >
                            <option value="any">Any Amount</option>
                            <option value="1-100">$1 - $100</option>
                            <option value="101-500">$101 - $500</option>
                            <option value="501+">$501+</option>
                        </select>
                        {(lapsedFilter !== 'any' || giftCountFilter !== 'any' || totalDonatedFilter !== 'any' || filter) && (
                            <button onClick={() => { setLapsedFilter('any'); setGiftCountFilter('any'); setTotalDonatedFilter('any'); setFilter(''); setSearchResults(null); }}
                                className="p-2 text-red-500 hover:text-red-700 text-sm transition-colors">
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider">
                            <tr>
                                <th className="p-3">Donor</th>
                                <th className="p-3">Email</th>
                                <th className="p-3">Phone</th>
                                <th className="p-3">Last Gift</th>
                                <th className="p-3">Status</th>
                                <th className="p-3 text-right">Gifts</th>
                                <th className="p-3 text-right">Total</th>
                                <th className="p-3 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDonors.slice((donorPage - 1) * DONOR_PAGE_SIZE, donorPage * DONOR_PAGE_SIZE).map(donor => {
                                const statusColors = {
                                    active:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                                    lapsing: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
                                    lapsed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                                    never:   'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400',
                                };
                                const statusLabels = { active: 'Active', lapsing: 'Lapsing', lapsed: 'Lapsed', never: 'Never' };
                                const isThanked = thankedIds.has(donor.id);
                                return (
                                <tr key={donor.id} className="border-b dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer" onClick={() => setViewingDonor(donor)}>
                                    <td className="p-3 font-medium dark:text-slate-200">{donor.name}</td>
                                    <td className="p-3 text-gray-500 dark:text-slate-400 text-sm">{donor.email}</td>
                                    <td className="p-3 text-gray-500 dark:text-slate-400 text-sm">{donor.phone || '—'}</td>
                                    <td className="p-3 text-xs text-slate-500 dark:text-slate-400">
                                        {donor.lastGiftDate ? new Date(donor.lastGiftDate).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td className="p-3">
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[donor.lapsedStatus]}`}>
                                            {statusLabels[donor.lapsedStatus]}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right dark:text-slate-300">{donor.giftCount}</td>
                                    <td className="p-3 text-right font-semibold dark:text-slate-200">${(donor.totalDonated ?? 0).toLocaleString()}</td>
                                    <td className="p-3" onClick={e => e.stopPropagation()}>
                                        <div className="flex justify-center gap-1">
                                            <button
                                                onClick={() => toggleThanked(donor.id)}
                                                title={isThanked ? 'Mark as not thanked' : 'Mark as thanked'}
                                                className={`p-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-1 ${isThanked ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                            >
                                                <Check size={14} />
                                                {isThanked ? 'Thanked' : 'Thank'}
                                            </button>
                                            <button
                                                className="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                onClick={() => setViewingDonor(donor)}
                                                title="View History"
                                            >
                                                <FileText size={16} />
                                            </button>
                                            <button
                                                className="p-1.5 text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                                                onClick={() => exportToVCard(donor)}
                                                title="Export to Contacts"
                                            >
                                                <UserPlus size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {filteredDonors.length === 0 && (
                        <EmptyState
                            message={`No donors found matching "${filter}"`}
                            icon={Search}
                        />
                    )}
                    {filteredDonors.length > DONOR_PAGE_SIZE && (
                        <div className="flex items-center justify-between px-4 py-3 border-t dark:border-slate-700">
                            <span className="text-sm text-gray-500 dark:text-slate-400">
                                Showing {(donorPage - 1) * DONOR_PAGE_SIZE + 1}–{Math.min(donorPage * DONOR_PAGE_SIZE, filteredDonors.length)} of {filteredDonors.length}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setDonorPage(p => Math.max(1, p - 1))}
                                    disabled={donorPage === 1}
                                    className="px-3 py-1 text-sm rounded-md bg-gray-100 dark:bg-slate-700 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-600"
                                >Prev</button>
                                <button
                                    onClick={() => setDonorPage(p => Math.min(Math.ceil(filteredDonors.length / DONOR_PAGE_SIZE), p + 1))}
                                    disabled={donorPage >= Math.ceil(filteredDonors.length / DONOR_PAGE_SIZE)}
                                    className="px-3 py-1 text-sm rounded-md bg-gray-100 dark:bg-slate-700 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-slate-600"
                                >Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {viewingDonor && (
                <Modal onClose={() => { setViewingDonor(null); setEditingDonor(false); }}>
                    {editingDonor ? (
                        <div className="space-y-4">
                            <h2 className="text-xl font-bold">Edit Donor</h2>
                            {[
                                { key: 'name', label: 'Name', type: 'text' },
                                { key: 'email', label: 'Email', type: 'email' },
                                { key: 'phone', label: 'Phone', type: 'tel' },
                            ].map(({ key, label, type }) => (
                                <div key={key}>
                                    <label className="block text-xs font-bold uppercase text-gray-400 mb-1">{label}</label>
                                    <input
                                        type={type}
                                        value={editFields[key]}
                                        onChange={e => setEditFields(prev => ({ ...prev, [key]: e.target.value }))}
                                        className="w-full px-3 py-2.5 border rounded-xl bg-gray-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200"
                                    />
                                </div>
                            ))}
                            <div className="flex gap-3 pt-2">
                                <button
                                    disabled={editSaving}
                                    onClick={async () => {
                                        setEditSaving(true);
                                        try {
                                            await api.updateDonor(viewingDonor.id, editFields);
                                            const updated = { ...viewingDonor, ...editFields };
                                            setViewingDonor(updated);
                                            setDonors(prev => prev.map(d => d.id === updated.id ? { ...d, ...editFields } : d));
                                            setEditingDonor(false);
                                        } catch (err) {
                                            alert('Save failed: ' + err.message);
                                        } finally {
                                            setEditSaving(false);
                                        }
                                    }}
                                    className="flex-1 py-3 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 disabled:opacity-50"
                                >{editSaving ? 'Saving...' : 'Save Changes'}</button>
                                <button onClick={() => setEditingDonor(false)} className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 font-semibold">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Donor Header */}
                            <div className="flex items-center gap-4 mb-5 pr-8">
                                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 shadow-lg">
                                    {(viewingDonor.name || '?').charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-xl font-bold truncate">{viewingDonor.name || <span className="text-gray-400 italic">No name</span>}</h2>
                                    {viewingDonor.email && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{viewingDonor.email}</p>}
                                    {viewingDonor.phone && <p className="text-sm text-gray-500 dark:text-gray-400">{viewingDonor.phone}</p>}
                                </div>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-2 gap-3 mb-5">
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide">Total Given</p>
                                    <p className="text-lg font-bold text-blue-700 dark:text-blue-300">${(viewingDonor.totalDonated ?? 0).toLocaleString()}</p>
                                </div>
                                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-3 text-center">
                                    <p className="text-xs text-purple-600 dark:text-purple-400 font-semibold uppercase tracking-wide">Gifts</p>
                                    <p className="text-lg font-bold text-purple-700 dark:text-purple-300">{viewingDonor.giftCount ?? 0}</p>
                                </div>
                            </div>

                            {/* Edit button */}
                            <button
                                onClick={() => { setEditFields({ name: viewingDonor.name || '', email: viewingDonor.email || '', phone: viewingDonor.phone || '' }); setEditingDonor(true); }}
                                className="w-full py-2.5 mb-5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 flex items-center justify-center gap-2 transition-colors"
                            >
                                <Edit2 size={15} /> Edit Donor Info
                            </button>

                            {/* Activity Timeline */}
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Activity Timeline</h3>
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-1 mb-5">
                                {(() => {
                                    const activities = [
                                        ...(notes[viewingDonor.id] || []).map(n => ({ ...n, type: 'note' })),
                                        ...(receipts[viewingDonor.id] || []).map(r => ({ ...r, text: `Donation of $${r.amount} to ${r.org}`, type: 'donation' })),
                                        ...pledges.filter(p => p.donorId === viewingDonor.id).map(p => ({ ...p, date: p.dueDate, text: `Pledge of $${p.amount} due`, type: 'pledge' }))
                                    ].sort((a, b) => new Date(b.date) - new Date(a.date));

                                    if (activities.length === 0) return <p className="text-sm text-gray-400 text-center py-4">No activity recorded yet.</p>;

                                    return activities.map((act, idx) => (
                                        <div key={idx} className="flex items-start gap-3">
                                            <div className={`mt-0.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                                act.type === 'donation' ? 'bg-green-500' : act.type === 'pledge' ? 'bg-blue-500' : 'bg-gray-400'
                                            }`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium leading-snug">{act.text}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">{new Date(act.date).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>

                            {/* Log interaction */}
                            <form onSubmit={handleAddNote} className="border-t dark:border-slate-700 pt-4">
                                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Log Interaction</label>
                                <textarea
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Add a note about this donor..."
                                    className="w-full px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-slate-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700 resize-none"
                                    rows="3"
                                />
                                <button type="submit" className="mt-3 w-full py-3 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors">
                                    Add Note
                                </button>
                            </form>
                        </>
                    )}
                </Modal>
            )}

            {showImport && (
                <Modal onClose={() => setShowImport(false)}>
                    <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                        <Upload className="text-green-500" />
                        Import Donors from CSV
                    </h2>
                    <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">CSV Format Requirements:</h3>
                            <p className="text-sm text-blue-700 dark:text-blue-400">
                                Your CSV file should include the following columns (in any order):
                                <br /><code className="bg-white dark:bg-gray-800 px-1 rounded">First Name, Last Name, Email, Phone, Amount, Member #, Date</code>
                            </p>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setImportFile(e.target.files[0] || null)}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {importing && (
                            <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-700 dark:text-blue-300 text-sm">
                                <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin shrink-0" />
                                Importing donors... this may take a minute for large files.
                            </div>
                        )}
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => { setShowImport(false); setImportFile(null); }}
                                disabled={importing}
                                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleImportCSV}
                                disabled={!importFile || importing}
                                className="px-4 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {importing ? (
                                    <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Importing...</>
                                ) : (
                                    <><Upload size={16} /> Import</>
                                )}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showDuplicates && (
                <DuplicatesModal
                    duplicates={duplicates}
                    onMerge={mergeDonors}
                    onMergeAll={mergeAllDuplicates}
                    mergingAll={mergingAll}
                    mergeProgress={mergeProgress}
                    onClose={() => setShowDuplicates(false)}
                />
            )}
        </div>
    );
};

const CampaignManagementPage = ({ campaigns, setCampaigns, setModal }) => {
    const [name, setName] = useState('');
    const [goal, setGoal] = useState('');
    const [importFile, setImportFile] = useState(null);

    const clearExamples = (prev) => prev.filter(c => !c._isExample);

    const handleSubmit = (e) => {
        e.preventDefault();
        const newCampaign = { id: `camp${Date.now()}`, name, goal: Number(goal), raised: 0 };
        setCampaigns(prev => [...clearExamples(prev), newCampaign]);
        setName('');
        setGoal('');
        setModal({ title: "Success", message: "New campaign has been created." });
    };

    const handleImportCSV = () => {
        if (!importFile) return;
        const parseCSVLine = (line) => {
            const result = [];
            let cur = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (ch === '"') { inQuotes = !inQuotes; }
                else if (ch === ',' && !inQuotes) { result.push(cur.trim()); cur = ''; }
                else { cur += ch; }
            }
            result.push(cur.trim());
            return result;
        };
        const reader = new FileReader();
        reader.onload = (event) => {
            const lines = event.target.result.split('\n');
            const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ' ').trim());
            const getCol = (values, ...names) => {
                for (const n of names) {
                    const idx = headers.indexOf(n);
                    if (idx !== -1 && values[idx]) return values[idx].trim();
                }
                return '';
            };
            const imported = [];
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;
                const values = parseCSVLine(lines[i]);
                const campName = getCol(values, 'name', 'campaign', 'campaign name');
                const campGoal = parseFloat(getCol(values, 'goal', 'goal ($)', 'target').replace(/[$,]/g, '')) || 0;
                const campRaised = parseFloat(getCol(values, 'raised', 'amount raised').replace(/[$,]/g, '')) || 0;
                if (!campName) continue;
                imported.push({ id: `camp${Date.now()}_${i}`, name: campName, goal: campGoal, raised: campRaised });
            }
            if (imported.length === 0) { alert('No valid rows found in CSV.'); return; }
            setCampaigns(prev => [...clearExamples(prev), ...imported]);
            setImportFile(null);
            alert(`Imported ${imported.length} campaign${imported.length !== 1 ? 's' : ''} successfully!`);
        };
        reader.readAsText(importFile);
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Campaign Management</h1>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="text-xl font-semibold">Create New Campaign</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                            type="text"
                            placeholder="Campaign Name"
                            className="md:col-span-2 p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                        <input
                            type="number"
                            placeholder="Goal ($)"
                            className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                            value={goal}
                            onChange={(e) => setGoal(e.target.value)}
                            required
                        />
                    </div>
                    <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600">Create Campaign</button>
                </form>
                <div className="border-t dark:border-gray-700 mt-4 pt-4 flex flex-wrap gap-2 items-center">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Or import from CSV:</span>
                    <label className="bg-green-500 text-white px-3 py-1 rounded-md text-sm cursor-pointer">
                        Choose CSV
                        <input type="file" accept=".csv" className="hidden" onChange={e => setImportFile(e.target.files[0] || null)} />
                    </label>
                    {importFile && <span className="text-sm text-gray-600 dark:text-gray-400">{importFile.name}</span>}
                    <button
                        className="bg-green-600 text-white px-3 py-1 rounded-md text-sm disabled:opacity-50"
                        onClick={handleImportCSV}
                        disabled={!importFile}
                    >
                        Import Campaigns
                    </button>
                    <span className="text-xs text-gray-400 w-full">Columns: <code>name</code>, <code>goal</code>, <code>raised</code> (optional)</span>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Active Campaigns</h3>
                <div className="space-y-4">
                    {campaigns.map(campaign => (
                        <div key={campaign.id} className={`p-4 border dark:border-gray-700 rounded-lg${campaign._isExample ? ' opacity-50' : ''}`}>
                            <div className="flex justify-between items-center mb-1">
                                <span className="font-semibold text-lg">{campaign.name}{campaign._isExample ? ' (example)' : ''}</span>
                                <span className="text-sm text-gray-600 dark:text-gray-400">${campaign.raised.toLocaleString()} / ${campaign.goal.toLocaleString()}</span>
                            </div>
                            <ProgressBar progress={campaign.goal > 0 ? (campaign.raised / campaign.goal) * 100 : 0} />
                            <button className="text-sm text-blue-500 hover:underline mt-2" onClick={() => setModal({ title: "Details", message: `Viewing details for ${campaign.name}` })}>
                                View Details
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- UPDATED Event Management Page ---
const EventManagementPage = ({ events, setEvents, setPage, setViewingEventId }) => {
    const [name, setName] = useState('');
    const [date, setDate] = useState('');
    const [location, setLocation] = useState('');
    const [goal, setGoal] = useState('');
    const [capacity, setCapacity] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        const newEvent = {
            id: `evt${Date.now()}`,
            name,
            date,
            location,
            goal: Number(goal),
            capacity: Number(capacity),
            registered: 0,
            raised: 0,
            attendees: []
        };
        setEvents(prev => [...prev, newEvent]);
        setName(''); setDate(''); setLocation(''); setGoal(''); setCapacity('');
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Event Management</h1>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="text-xl font-semibold">Create New Event</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" placeholder="Event Name" value={name} onChange={e => setName(e.target.value)} className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" required />
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" required />
                    </div>
                    <input type="text" placeholder="Location (e.g., Grand Ballroom)" value={location} onChange={e => setLocation(e.target.value)} className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" required />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="number" placeholder="Fundraising Goal ($)" value={goal} onChange={e => setGoal(e.target.value)} className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" />
                        <input type="number" placeholder="Capacity (e.g., 150)" value={capacity} onChange={e => setCapacity(e.target.value)} className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600">Create Event</button>
                </form>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Upcoming Events</h3>
                <div className="space-y-4">
                    {events.map(event => (
                        <div key={event.id} className="p-4 border dark:border-gray-700 rounded-lg">
                            <div className="flex justify-between items-start">
                                <div>
                                    <span className="font-semibold text-lg">{event.name}</span>
                                    <p className="text-sm text-gray-500">{new Date(event.date).toLocaleDateString()} - {event.location}</p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <p className="font-semibold">{event.registered} / {event.capacity} Registered</p>
                                    <p className="text-sm text-green-600">${event.raised.toLocaleString()} Raised</p>
                                </div>
                            </div>
                            <button className="text-sm text-blue-500 hover:underline mt-2" onClick={() => setViewingEventId(event.id)}>
                                View Details
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const EventDetailsPage = ({ event, setModal, onBack }) => {
    const [eventTab, setEventTab] = useState('details');
    
    return (
        <div>
            <button onClick={onBack} className="text-sm font-medium text-blue-500 hover:underline mb-4 flex items-center gap-1">
                <ChevronLeft size={16} /> Back to Events
            </button>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h1 className="text-3xl font-bold mb-2">{event.name}</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">{new Date(event.date).toLocaleDateString()} | {event.location}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Raised</p>
                        <p className="text-2xl font-bold">${event.raised.toLocaleString()} / ${event.goal.toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Registered</p>
                        <p className="text-2xl font-bold">{event.registered} / {event.capacity}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg flex items-center justify-center">
                        <button 
                            className="w-full bg-green-500 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2"
                            onClick={() => setModal({ title: "Register", message: "This would open a public registration form for the event." })}
                        >
                            <Ticket size={18} /> Register for Event
                        </button>
                    </div>
                </div>

                <div className="flex border-b dark:border-gray-700 overflow-x-auto">
                    <TabButton name="details" label="Details" activeTab={eventTab} setTab={setEventTab} />
                    <TabButton name="attendees" label={`Attendees (${event.attendees.length})`} activeTab={eventTab} setTab={setEventTab} />
                    <TabButton name="donations" label="Donations" activeTab={eventTab} setTab={setEventTab} />
                </div>
                
                <div className="p-6">
                    {eventTab === 'details' && (
                        <p>This area would contain the event description, speaker bios, and other details.</p>
                    )}
                    {eventTab === 'attendees' && (
                        <div>
                            <h3 className="text-xl font-semibold mb-4">Attendee List</h3>
                            <ul className="space-y-2">
                                {event.attendees.map(attendee => (
                                    <li key={attendee.id} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-md">{attendee.name}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {eventTab === 'donations' && (
                         <p>This area would list all donations tied specifically to this event.</p>
                    )}
                </div>
            </div>
        </div>
    );
};


const PledgeManagementPage = ({ pledges, setPledges, donors, setDonors, setModal }) => {
    const [selectedDonor, setSelectedDonor] = useState('');
    const [donorSearchTerm, setDonorSearchTerm] = useState('');
    const [showNewDonorForm, setShowNewDonorForm] = useState(false);
    const [newDonorName, setNewDonorName] = useState('');
    const [newDonorEmail, setNewDonorEmail] = useState('');
    const [newDonorPhone, setNewDonorPhone] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const filteredDonors = donors.filter(d =>
        (d.name || '').toLowerCase().includes(donorSearchTerm.toLowerCase()) ||
        (d.email || '').toLowerCase().includes(donorSearchTerm.toLowerCase()) ||
        (d.phone || '').includes(donorSearchTerm)
    );

    // Fuzzy check for existing donor
    const duplicateWarning = useMemo(() => {
        if (!newDonorName || newDonorName.length < 3) return null;
        return donors.find(d =>
            (d.name && d.name.toLowerCase().includes(newDonorName.toLowerCase())) ||
            (newDonorEmail && d.email && d.email.toLowerCase() === newDonorEmail.toLowerCase())
        );
    }, [newDonorName, newDonorEmail, donors]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Logic to add a new pledge
        setModal({ title: "Pledge Logged", message: "The new pledge has been recorded." });
    };

    const handleAddNewDonor = async () => {
        if (!newDonorName) {
            alert('Name is required');
            return;
        }
        if (!newDonorEmail && !newDonorPhone) {
            alert('Please provide at least an email or phone number');
            return;
        }
        try {
            const { id } = await api.addDonor({ name: newDonorName, email: newDonorEmail, phone: newDonorPhone });
            const { donors: apiDonors } = await api.getDonors();
            setDonors(apiDonors.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 })));
            setSelectedDonor(id);
            setShowNewDonorForm(false);
            setNewDonorName('');
            setNewDonorEmail('');
            setNewDonorPhone('');
            setModal({ title: "Donor Added", message: `${newDonorName} has been added to your donor list.` });
        } catch (err) {
            alert('Failed to add donor: ' + err.message);
        }
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Pledge Management</h1>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="text-xl font-semibold">Log New Pledge</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search for donor..."
                                value={donorSearchTerm}
                                onChange={(e) => {
                                    setDonorSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                className="w-full p-2 pl-10 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            {showDropdown && donorSearchTerm && (
                                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    {filteredDonors.length > 0 ? (
                                        filteredDonors.map(donor => (
                                            <div
                                                key={donor.id}
                                                onClick={() => {
                                                    setSelectedDonor(donor.id);
                                                    setDonorSearchTerm(donor.name);
                                                    setShowDropdown(false);
                                                    setShowNewDonorForm(false);
                                                }}
                                                className="p-3 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer border-b dark:border-gray-700 last:border-b-0"
                                            >
                                                <p className="font-medium">{donor.name}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">{donor.email}</p>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-3 text-gray-500">No donors found</div>
                                    )}
                                    <div
                                        onClick={() => {
                                            setShowNewDonorForm(true);
                                            setShowDropdown(false);
                                        }}
                                        className="p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer font-semibold text-blue-600 flex items-center gap-2"
                                    >
                                        <UserPlus size={18} />
                                        Add New Donor
                                    </div>
                                </div>
                            )}
                        </div>
                        <input type="number" placeholder="Amount ($)" className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" />
                        <input type="date" className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    {showNewDonorForm && (
                        <div className="border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                            <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                                <UserPlus size={18} />
                                Add New Donor
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <input
                                    type="text"
                                    placeholder="Full Name *"
                                    value={newDonorName}
                                    onChange={(e) => setNewDonorName(e.target.value)}
                                    className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                />
                                {duplicateWarning && (
                                    <div className="md:col-span-3 text-xs text-orange-600 flex items-center gap-1 font-medium">
                                        <AlertCircle size={14} />
                                        Note: A donor with a similar name ({duplicateWarning.name}) already exists.
                                    </div>
                                )}
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={newDonorEmail}
                                    onChange={(e) => setNewDonorEmail(e.target.value)}
                                    className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                />
                                <input
                                    type="tel"
                                    placeholder="Phone"
                                    value={newDonorPhone}
                                    onChange={(e) => setNewDonorPhone(e.target.value)}
                                    className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                />
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    type="button"
                                    onClick={handleAddNewDonor}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                                >
                                    <Check size={16} />
                                    Add Donor
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowNewDonorForm(false)}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600">Log Pledge</button>
                </form>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md overflow-x-auto">
                 <table className="w-full text-left min-w-[400px]">
                    <thead className="border-b dark:border-gray-700">
                        <tr>
                            <th className="p-3">Donor</th>
                            <th className="p-3">Amount</th>
                            <th className="p-3">Due Date</th>
                            <th className="p-3">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pledges.map(pledge => (
                            <tr key={pledge.id} className="border-b dark:border-gray-700">
                                <td className="p-3 font-medium">{pledge.donorName}</td>
                                <td className="p-3">${pledge.amount.toLocaleString()}</td>
                                <td className="p-3">{new Date(pledge.dueDate).toLocaleDateString()}</td>
                                <td className="p-3">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                        pledge.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                    }`}>
                                        {pledge.status}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ManualDonationPage = ({ donors, setDonors, campaigns, setDonations, setModal, checkWorkflows, setReceipts }) => {
    const [donationType, setDonationType] = useState('Cash');
    const [selectedDonorId, setSelectedDonorId] = useState('');
    const [donorSearchTerm, setDonorSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNewDonorForm, setShowNewDonorForm] = useState(false);
    const [newDonorName, setNewDonorName] = useState('');
    const [newDonorEmail, setNewDonorEmail] = useState('');
    const [newDonorPhone, setNewDonorPhone] = useState('');
    const [amount, setAmount] = useState('');
    const [campaignId, setCampaignId] = useState('');
    const [donationNotes, setDonationNotes] = useState('');

    const filteredDonors = donors.filter(d =>
        (d.name || '').toLowerCase().includes(donorSearchTerm.toLowerCase()) ||
        (d.email || '').toLowerCase().includes(donorSearchTerm.toLowerCase()) ||
        (d.phone || '').includes(donorSearchTerm)
    );

    const duplicateWarning = useMemo(() => {
        if (!newDonorName || newDonorName.length < 3) return null;
        return donors.find(d =>
            (d.name && d.name.toLowerCase().includes(newDonorName.toLowerCase())) ||
            (newDonorEmail && d.email && d.email.toLowerCase() === newDonorEmail.toLowerCase())
        );
    }, [newDonorName, newDonorEmail, donors]);

    const handleAddNewDonor = async () => {
        if (!newDonorName) { alert('Name is required'); return; }
        if (!newDonorEmail && !newDonorPhone) { alert('Please provide at least an email or phone number'); return; }
        try {
            const { id } = await api.addDonor({ name: newDonorName, email: newDonorEmail, phone: newDonorPhone });
            const { donors: apiDonors } = await api.getDonors();
            setDonors(apiDonors.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 })));
            setSelectedDonorId(id);
            setDonorSearchTerm(newDonorName);
            setShowNewDonorForm(false);
            setNewDonorName(''); setNewDonorEmail(''); setNewDonorPhone('');
            setModal({ title: "Donor Added", message: `${newDonorName} has been added to your donor list.` });
        } catch (err) {
            alert('Failed to add donor: ' + err.message);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const amt = parseFloat(amount) || 0;
        if (!selectedDonorId || amt <= 0) return;

        const donor = donors.find(d => d.id === selectedDonorId);

        // 1. Update Donor Totals
        setDonations(prev => prev.map(d => 
            d.id === selectedDonorId 
                ? { ...d, totalDonated: d.totalDonated + amt, giftCount: d.giftCount + 1 }
                : d
        ));

        // 2. Record Receipt for Timeline
        const newReceipt = {
            id: `manual-${Date.now()}`,
            org: 'Non-Profit Inc.',
            amount: amt,
            date: new Date().toISOString().split('T')[0],
            pdfUrl: '#'
        };
        setReceipts(prev => ({
            ...prev,
            [selectedDonorId]: [newReceipt, ...(prev[selectedDonorId] || [])]
        }));

        checkWorkflows({ amount: amt });
        setModal({ title: "Donation Recorded", message: `Successfully added $${amt.toLocaleString()} to ${donor.name}'s history.` });
        
        // Reset form
        setAmount('');
        setSelectedDonorId('');
        setDonationNotes('');
    };
    
    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Add Manual Donation</h1>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md max-w-2xl mx-auto">
                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Select Donor</label>
                            <div className="relative mt-1">
                                <input
                                    type="text"
                                    placeholder="Search by name, email, or phone..."
                                    value={donorSearchTerm}
                                    onChange={(e) => { setDonorSearchTerm(e.target.value); setShowDropdown(true); setSelectedDonorId(''); }}
                                    onFocus={() => setShowDropdown(true)}
                                    className="w-full p-2 pl-10 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    required={!selectedDonorId}
                                />
                                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                {selectedDonorId && (
                                    <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />
                                )}
                                {showDropdown && donorSearchTerm && (
                                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                        {filteredDonors.length > 0 ? (
                                            filteredDonors.map(donor => (
                                                <div
                                                    key={donor.id}
                                                    onMouseDown={() => {
                                                        setSelectedDonorId(donor.id);
                                                        setDonorSearchTerm(donor.name);
                                                        setShowDropdown(false);
                                                        setShowNewDonorForm(false);
                                                    }}
                                                    className="p-3 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer border-b dark:border-gray-700 last:border-b-0"
                                                >
                                                    <p className="font-medium">{donor.name}</p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">{donor.email}</p>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-3 text-gray-500">No donors found</div>
                                        )}
                                        <div
                                            onMouseDown={() => { setShowNewDonorForm(true); setShowDropdown(false); }}
                                            className="p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer font-semibold text-blue-600 flex items-center gap-2"
                                        >
                                            <UserPlus size={18} />
                                            Add New Donor
                                        </div>
                                    </div>
                                )}
                            </div>
                            {showNewDonorForm && (
                                <div className="mt-3 border-2 border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                                    <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                                        <UserPlus size={18} />
                                        Add New Donor
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <input
                                            type="text"
                                            placeholder="Full Name *"
                                            value={newDonorName}
                                            onChange={(e) => setNewDonorName(e.target.value)}
                                            className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                        />
                                        {duplicateWarning && (
                                            <div className="md:col-span-3 text-xs text-orange-600 flex items-center gap-1 font-medium">
                                                <AlertCircle size={14} />
                                                Note: A donor with a similar name ({duplicateWarning.name}) already exists.
                                            </div>
                                        )}
                                        <input
                                            type="email"
                                            placeholder="Email"
                                            value={newDonorEmail}
                                            onChange={(e) => setNewDonorEmail(e.target.value)}
                                            className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                        />
                                        <input
                                            type="tel"
                                            placeholder="Phone"
                                            value={newDonorPhone}
                                            onChange={(e) => setNewDonorPhone(e.target.value)}
                                            className="p-2 border rounded-md bg-white dark:bg-gray-700 dark:border-gray-600"
                                        />
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <button type="button" onClick={handleAddNewDonor} className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2">
                                            <Check size={16} /> Add Donor
                                        </button>
                                        <button type="button" onClick={() => setShowNewDonorForm(false)} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Donation Type</label>
                                <select 
                                    className="mt-1 block w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    value={donationType}
                                    onChange={(e) => setDonationType(e.target.value)}
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Check">Check</option>
                                    <option value="Credit Card">Credit Card (Offline)</option>
                                    <option value="In-Kind">In-Kind</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount ($)</label>
                                <input 
                                    type="number" 
                                    placeholder="100.00" 
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    className="mt-1 block w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" 
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Assign to Campaign</label>
                            <select 
                                value={campaignId}
                                onChange={e => setCampaignId(e.target.value)}
                                className="mt-1 block w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                            >
                                <option value="">General Fund</option>
                                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes (Optional)</label>
                            <textarea 
                                rows="3" 
                                value={donationNotes}
                                onChange={e => setDonationNotes(e.target.value)}
                                placeholder="Donation given at annual gala..." 
                                className="mt-1 block w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                            />
                        </div>
                        
                        <div className="text-right">
                            <button type="submit" className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600">Record Donation</button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

const DEFAULT_PRESET_AMOUNTS = [18, 36, 180, 360];

const PAYMENT_METHODS = [
    { id: 'apple',  label: 'Apple Pay',  icon: '🍎', express: true },
    { id: 'google', label: 'Google Pay', icon: 'G',  express: true },
    { id: 'paypal', label: 'PayPal',     icon: '🅿',  express: true },
    { id: 'venmo',  label: 'Venmo',      icon: '✌',  express: true },
    { id: 'cash',   label: 'Cash App',  icon: '$',  express: true },
    { id: 'daf',    label: 'DAF',        icon: '🏦', express: true },
    { id: 'card',   label: 'Card',       icon: '💳', express: false },
];

const PublicDonationPage = ({ setModal, checkWorkflows, presetAmounts = DEFAULT_PRESET_AMOUNTS }) => {
    const [amount, setAmount] = useState(presetAmounts[0]);
    const [customAmount, setCustomAmount] = useState('');
    const [coverFee, setCoverFee] = useState(true);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [frequency, setFrequency] = useState('One-Time');
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [submitting, setSubmitting] = useState(false);
    const [einSearch, setEinSearch] = useState('');
    const [searchType, setSearchType] = useState('name');
    const [searching, setSearching] = useState(false);
    const [nonprofitInfo, setNonprofitInfo] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [orgOpen, setOrgOpen] = useState(true);

    const activeAmount = customAmount !== '' ? (parseFloat(customAmount) || 0) : amount;
    const [applePayAvailable] = useState(() => !!(window.ApplePaySession && window.ApplePaySession.canMakePayments?.()));
    const [googlePayAvailable] = useState(() => !!(window.PaymentRequest));

    const fee = activeAmount * 0.027;
    const total = coverFee ? activeAmount + fee : activeAmount;

    const handleEINSearch = async (e) => {
        e.preventDefault();
        if (!einSearch.trim()) return;

        setSearching(true);
        setNonprofitInfo(null);
        setSearchResults([]);

        try {
            if (searchType === 'ein') {
                // Search by EIN - using CORS proxy to access ProPublica API
                const cleanEin = einSearch.replace(/[^0-9]/g, ''); // Remove dashes

                if (cleanEin.length !== 9) {
                    setModal({ title: "Invalid EIN", message: "EIN must be 9 digits (format: 12-3456789 or 123456789)" });
                    setSearching(false);
                    return;
                }

                // Use CORS proxy to access ProPublica Nonprofit Explorer API
                const proxyUrl = 'https://corsproxy.io/?';
                const apiUrl = `https://projects.propublica.org/nonprofits/api/v2/organizations/${cleanEin}.json`;

                try {
                    const response = await fetch(proxyUrl + encodeURIComponent(apiUrl));

                    if (response.ok) {
                        const data = await response.json();
                        const org = data.organization;

                        setNonprofitInfo({
                            ein: org.ein,
                            name: org.name,
                            city: org.city || 'N/A',
                            state: org.state || 'N/A',
                            category: `${org.ntee_code || 'Non-Profit'} - ${org.subsection_code || '501(c)(3)'}`,
                            verified: true,
                            address: org.address || '',
                            revenue: org.revenue_amount,
                            assets: org.asset_amount
                        });
                    } else {
                        setModal({
                            title: "Not Found",
                            message: "No non-profit found with that EIN. The organization may not be in the IRS database, may be inactive, or the EIN may be incorrect."
                        });
                    }
                } catch (proxyError) {
                    console.error('Proxy API error:', proxyError);
                    setModal({
                        title: "Search Error",
                        message: "Unable to connect to the non-profit database. Please try again in a moment."
                    });
                }
            } else {
                // Search by name using ProPublica API
                const proxyUrl = 'https://corsproxy.io/?';
                const apiUrl = `https://projects.propublica.org/nonprofits/api/v2/search.json?q=${encodeURIComponent(einSearch)}`;

                try {
                    const response = await fetch(proxyUrl + encodeURIComponent(apiUrl));

                    if (response.ok) {
                        const data = await response.json();

                        if (data.organizations && data.organizations.length > 0) {
                            setSearchResults(data.organizations.slice(0, 10).map(org => ({
                                ein: org.ein,
                                name: org.name,
                                city: org.city || 'N/A',
                                state: org.state || 'N/A',
                                category: org.ntee_code || 'Non-Profit',
                                verified: true
                            })));
                        } else {
                            setModal({
                                title: "No Results",
                                message: "No organizations found matching that name. Try a different search term or check your spelling."
                            });
                        }
                    } else {
                        throw new Error('Search failed');
                    }
                } catch (nameSearchError) {
                    console.error('Name search error:', nameSearchError);
                    setModal({
                        title: "Search Error",
                        message: "Unable to search by name at this time. Please try searching by EIN instead."
                    });
                }
            }
        } catch (error) {
            console.error('Search error:', error);
            setModal({
                title: "Search Error",
                message: "An error occurred during search. Please try again."
            });
        } finally {
            setSearching(false);
        }
    };

    const selectOrganization = async (ein) => {
        setSearching(true);
        setSearchResults([]);

        try {
            const cleanEin = ein.replace(/[^0-9]/g, '');
            const proxyUrl = 'https://corsproxy.io/?';
            const apiUrl = `https://projects.propublica.org/nonprofits/api/v2/organizations/${cleanEin}.json`;

            const response = await fetch(proxyUrl + encodeURIComponent(apiUrl));

            if (response.ok) {
                const data = await response.json();
                const org = data.organization;

                setNonprofitInfo({
                    ein: org.ein,
                    name: org.name,
                    city: org.city || 'N/A',
                    state: org.state || 'N/A',
                    category: `${org.ntee_code || 'Non-Profit'} - ${org.subsection_code || '501(c)(3)'}`,
                    verified: true,
                    address: org.address || '',
                    revenue: org.revenue_amount,
                    assets: org.asset_amount
                });
            }
        } catch (error) {
            console.error('Selection error:', error);
        } finally {
            setSearching(false);
        }
    };

    const resetForm = () => {
        setAmount(presetAmounts[0]); setCustomAmount(''); setCoverFee(true);
        setFirstName(''); setLastName(''); setEmail(''); setPhone('');
        setFrequency('One-Time'); setPaymentMethod('card');
    };

    const handleExpressPayment = (methodId) => {
        if (!nonprofitInfo) {
            setModal({ title: 'Select an Organization', message: 'Please search and select a nonprofit before donating.' });
            return;
        }
        setModal({
            title: `${PAYMENT_METHODS.find(m => m.id === methodId)?.label} Checkout`,
            message: `Redirecting to ${PAYMENT_METHODS.find(m => m.id === methodId)?.label} to complete your $${total.toFixed(2)} donation to ${nonprofitInfo.name}.`
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.submitDonation({
                name: `${firstName} ${lastName}`.trim(),
                email,
                phone,
                amount: activeAmount,
                fee: coverFee ? fee : 0,
                total,
                paymentMethod,
                recurring: frequency !== 'One-Time',
                frequency: frequency !== 'One-Time' ? frequency : null,
                nonprofitName: nonprofitInfo?.name || null,
                nonprofitEin: nonprofitInfo?.ein || null,
            });
            checkWorkflows({ amount: total });
            setModal({
                title: `Thank you, ${firstName || 'Friend'}!`,
                message: `Your ${frequency.toLowerCase()} donation of $${total.toFixed(2)} has been processed. A receipt will be sent to ${email}.`
            });
            resetForm();
        } catch (err) {
            setModal({ title: 'Error', message: `Submission failed: ${err.message}` });
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition";

    return (
        <div className="max-w-lg mx-auto">
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Make a Difference</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Fast, secure giving to verified nonprofits</p>
            </div>

            {/* Org Search Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 mb-4 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setOrgOpen(o => !o)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                    <span className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-200">
                        <Search size={18} className="text-blue-500" />
                        {nonprofitInfo ? nonprofitInfo.name : 'Select a Nonprofit'}
                    </span>
                    <span className="text-xs text-gray-400">{orgOpen ? '▲' : '▼'}</span>
                </button>

                {nonprofitInfo && !orgOpen && (
                    <div className="px-5 pb-3 -mt-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full flex items-center gap-1">
                            <Check size={11} /> Verified
                        </span>
                        <span className="text-xs text-gray-400">{nonprofitInfo.city}, {nonprofitInfo.state}</span>
                    </div>
                )}

                {orgOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                        <div className="flex gap-2 mb-3">
                            {['name', 'ein'].map(t => (
                                <button key={t} type="button" onClick={() => setSearchType(t)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${searchType === t ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                    By {t === 'ein' ? 'EIN' : 'Name'}
                                </button>
                            ))}
                        </div>
                        <form onSubmit={handleEINSearch} className="flex gap-2">
                            <input
                                type="text"
                                placeholder={searchType === 'ein' ? 'e.g. 13-1623877' : 'e.g. Red Cross'}
                                value={einSearch}
                                onChange={e => setEinSearch(e.target.value)}
                                className={inputCls}
                            />
                            <button type="submit" disabled={searching || !einSearch}
                                className="px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap transition">
                                {searching ? '…' : 'Search'}
                            </button>
                        </form>

                        {searchResults.length > 0 && (
                            <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
                                {searchResults.map(org => (
                                    <div key={org.ein} onClick={() => { selectOrganization(org.ein); setOrgOpen(false); }}
                                        className="p-3 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition">
                                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{org.name}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{org.city}, {org.state} · EIN {org.ein}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {nonprofitInfo && (
                            <div className="mt-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="font-semibold text-green-800 dark:text-green-300 text-sm flex items-center gap-1">
                                            <Check size={14} /> {nonprofitInfo.name}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-0.5">{nonprofitInfo.city}, {nonprofitInfo.state} · EIN {nonprofitInfo.ein}</p>
                                    </div>
                                    <button type="button" onClick={() => setOrgOpen(false)}
                                        className="text-xs text-blue-600 font-medium ml-2 shrink-0">Done</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Main Donation Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">

                {/* Frequency */}
                <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600 mb-6">
                    {['One-Time', 'Monthly', 'Annually'].map((f, i) => (
                        <button key={f} type="button" onClick={() => setFrequency(f)}
                            className={`flex-1 py-2.5 text-sm font-semibold transition ${frequency === f ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'} ${i !== 0 ? 'border-l border-gray-200 dark:border-gray-600' : ''}`}>
                            {f}
                        </button>
                    ))}
                </div>

                {/* Amount */}
                <div className="mb-6">
                    <div className="grid grid-cols-4 gap-2 mb-3">
                        {presetAmounts.map(val => (
                            <button key={val} type="button"
                                onClick={() => { setAmount(val); setCustomAmount(''); }}
                                className={`py-3 rounded-xl text-sm font-bold transition ${activeAmount === val && customAmount === '' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-600'}`}>
                                ${val}
                            </button>
                        ))}
                    </div>
                    <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                        <input type="number" min="1" placeholder="Custom amount"
                            value={customAmount}
                            onChange={e => setCustomAmount(e.target.value)}
                            className={`${inputCls} pl-8`} />
                    </div>
                </div>

                {/* Express Pay */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                        <span className="text-xs text-gray-400 font-medium">Express checkout</span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {PAYMENT_METHODS.filter(m => m.express).map(m => {
                            const hidden = (m.id === 'apple' && !applePayAvailable) || (m.id === 'google' && !googlePayAvailable);
                            if (hidden) return null;
                            return (
                                <button key={m.id} type="button" onClick={() => handleExpressPayment(m.id)}
                                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-600 transition text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    <span>{m.icon}</span> <span className="text-xs">{m.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Card Form */}
                <form onSubmit={handleSubmit}>
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                        <span className="text-xs text-gray-400 font-medium">Or pay by card</span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-gray-600" />
                    </div>

                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <input type="text" placeholder="First name" required
                                autoComplete="given-name"
                                value={firstName} onChange={e => setFirstName(e.target.value)}
                                className={inputCls} />
                            <input type="text" placeholder="Last name" required
                                autoComplete="family-name"
                                value={lastName} onChange={e => setLastName(e.target.value)}
                                className={inputCls} />
                        </div>
                        <input type="email" placeholder="Email address" required
                            autoComplete="email"
                            value={email} onChange={e => setEmail(e.target.value)}
                            className={inputCls} />
                        <input type="tel" placeholder="Phone (optional)"
                            autoComplete="tel"
                            value={phone} onChange={e => setPhone(e.target.value)}
                            className={inputCls} />
                    </div>

                    {/* Fee & Total */}
                    <div className="mt-5 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={coverFee} onChange={e => setCoverFee(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded text-blue-600 border-gray-300" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                                Cover the 2.7% processing fee <span className="text-gray-400">(+${fee.toFixed(2)})</span> so 100% reaches the nonprofit
                            </span>
                        </label>
                        <div className="mt-3 flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-500">Total</span>
                            <span className="text-2xl font-bold text-blue-600">${total.toFixed(2)}</span>
                        </div>
                    </div>

                    <button type="submit" disabled={submitting || activeAmount <= 0}
                        className="mt-4 w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl font-bold text-lg transition shadow-md">
                        {submitting ? 'Processing…' : `Donate $${total.toFixed(2)} →`}
                    </button>
                    <p className="text-center text-xs text-gray-400 mt-3">🔒 Secure & encrypted · Tax-deductible receipt sent by email</p>
                </form>
            </div>
        </div>
    );
};

// --- UPDATED AdminPortalPage ---
const AdminPortalPage = ({ users, setUsers, tasks, setTasks, grants, setGrants, volunteers, donors, setModal, workflows, setWorkflows, setPage, setViewingGrantId }) => {
    const [adminTab, setAdminTab] = useState('users');
    const [editingUser, setEditingUser] = useState(null);

    const handleSaveUser = (updatedUser) => {
        setUsers(prevUsers => prevUsers.map(u => u.id === updatedUser.id ? updatedUser : u));
        setEditingUser(null);
    };

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Admin Portal</h1>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="flex border-b dark:border-gray-700 overflow-x-auto">
                    <TabButton name="users" label="User Management" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="volunteers" label="Volunteer Management" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="tasks" label="Task Management" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="grants" label="Grant Tracking" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="workflows" label="Workflows" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="email" label="Communications" activeTab={adminTab} setTab={setAdminTab} />
                    <TabButton name="integrations" label="Integrations" activeTab={adminTab} setTab={setAdminTab} />
                </div>
                
                <div className="p-6">
                    {adminTab === 'users' && <UserManagementTab users={users} setUsers={setUsers} setModal={setModal} setEditingUser={setEditingUser} />}
                    {adminTab === 'volunteers' && <VolunteerManagementTab volunteers={volunteers} tasks={tasks} setModal={setModal} />}
                    {adminTab === 'tasks' && <TaskManagementTab tasks={tasks} setTasks={setTasks} volunteers={volunteers} donors={donors} setModal={setModal} />}
                    {adminTab === 'grants' && <GrantTrackingTab grants={grants} setGrants={setGrants} setViewingGrantId={setViewingGrantId} />}
                    {adminTab === 'workflows' && <WorkflowsTab workflows={workflows} setWorkflows={setWorkflows} volunteers={volunteers} setModal={setModal} />}
                    {adminTab === 'email' && <CommunicationsTab donors={donors} setModal={setModal} />}
                    {adminTab === 'integrations' && <IntegrationsTab />}
                </div>
            </div>
            
            {editingUser && (
                <EditUserModal 
                    user={editingUser} 
                    onClose={() => setEditingUser(null)}
                    onSave={handleSaveUser}
                />
            )}
        </div>
    );
};

// --- UPDATED UserManagementTab ---
const UserManagementTab = ({ users, setUsers, setModal, setEditingUser }) => {
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);

    const parseCSVLine = (line) => {
        const result = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQuotes = !inQuotes; }
            else if (ch === ',' && !inQuotes) { result.push(cur.trim()); cur = ''; }
            else { cur += ch; }
        }
        result.push(cur.trim());
        return result;
    };

    const handleImportCSV = async () => {
        if (!importFile) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                const lines = text.split('\n');
                const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, ' ').trim());
                const getCol = (values, ...names) => {
                    for (const n of names) {
                        const idx = headers.indexOf(n);
                        if (idx !== -1 && values[idx]) return values[idx].trim();
                    }
                    return '';
                };

                const toImport = [];
                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;
                    const values = parseCSVLine(lines[i]);
                    let name = getCol(values, 'name', 'full name');
                    if (!name) {
                        const first = getCol(values, 'first name', 'firstname');
                        const last = getCol(values, 'last name', 'lastname');
                        name = [first, last].filter(Boolean).join(' ');
                    }
                    const email = getCol(values, 'email', 'email address');
                    const phone = getCol(values, 'phone', 'phone number', 'tel');
                    const role = getCol(values, 'role') || 'donor';
                    if (!name && !email && !phone) continue;
                    toImport.push({ name, email, phone, role });
                }

                if (toImport.length === 0) { alert('No valid rows found in CSV.'); return; }

                const result = await api.bulkImportUsers(toImport);
                const { users: apiUsers } = await api.getUsers();
                if (apiUsers.length > 0) setUsers(apiUsers);
                setImportFile(null);
                const parts = [];
                if (result.duplicates > 0) parts.push(`${result.duplicates} already in database`);
                if (result.blank > 0) parts.push(`${result.blank} rows had no contact info`);
                alert(`Imported ${result.saved} new users successfully!${parts.length > 0 ? '\n\nSkipped: ' + parts.join(', ') + '.' : ''}`);
            } catch (err) {
                alert('Import failed: ' + err.message);
            } finally {
                setImporting(false);
            }
        };
        reader.readAsText(importFile);
    };

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">User Management</h3>
            <div className="flex flex-wrap gap-2 mb-4 items-center">
                <label className="bg-green-500 text-white px-3 py-1 rounded-md text-sm cursor-pointer">
                    Choose CSV
                    <input type="file" accept=".csv" className="hidden" onChange={e => setImportFile(e.target.files[0] || null)} />
                </label>
                {importFile && <span className="text-sm text-gray-600 dark:text-gray-400">{importFile.name}</span>}
                <button
                    className="bg-green-600 text-white px-3 py-1 rounded-md text-sm disabled:opacity-50"
                    onClick={handleImportCSV}
                    disabled={!importFile || importing}
                >
                    {importing ? 'Importing…' : 'Import Users'}
                </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">CSV columns: <code>name</code> (or <code>first name</code> + <code>last name</code>), <code>email</code>, <code>phone</code>, <code>role</code> (defaults to <em>donor</em>)</p>
            <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[400px]">
                <thead className="border-b dark:border-gray-700"><tr><th className="p-2">Name</th><th className="p-2">Role</th><th className="p-2">Phone</th><th className="p-2">Actions</th></tr></thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.id} className={user._isExample ? 'opacity-50 italic' : ''}>
                            <td className="p-2">{user.name}{user._isExample ? ' (example)' : ''}</td>
                            <td className="p-2">{user.role}</td>
                            <td className="p-2">{user.phone}</td>
                            <td className="p-2">
                                <button
                                    className="text-sm text-blue-500 hover:underline"
                                    onClick={() => setEditingUser(user)}
                                >
                                    Edit
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
};

// --- NEW EditUserModal ---
const EditUserModal = ({ user, onClose, onSave }) => {
    const [name, setName] = useState(user.name);
    const [phone, setPhone] = useState(user.phone);
    const [role, setRole] = useState(user.role);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ ...user, name, phone, role });
    };

    return (
        <Modal onClose={onClose}>
            <form onSubmit={handleSubmit}>
                <h2 className="text-xl font-bold mb-4">Edit User: {user.name}</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium">Name</label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Phone</label>
                        <input 
                            type="tel" 
                            value={phone} 
                            onChange={e => setPhone(e.target.value)} 
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Role</label>
                        <select 
                            value={role} 
                            onChange={e => setRole(e.target.value)} 
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                        >
                            <option value="Admin">Admin</option>
                            <option value="Volunteer">Volunteer</option>
                            <option value="Donor">Donor (Read-Only)</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end mt-6 gap-3">
                    <button type="button" className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 px-6 py-2 rounded-lg font-semibold" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="submit" className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600">
                        Save Changes
                    </button>
                </div>
            </form>
        </Modal>
    );
};


const VolunteerManagementTab = ({ volunteers, tasks, setModal }) => (
    <div>
        <h3 className="text-xl font-semibold mb-4">Volunteer Management</h3>
        <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[500px]">
            <thead className="border-b dark:border-gray-700"><tr><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Phone</th><th className="p-2">Tasks</th><th className="p-2">Actions</th></tr></thead>
            <tbody>
                {volunteers.map(vol => {
                    const assignedTasks = tasks.filter(t => t.assigneeId === vol.id);
                    return (
                        <tr key={vol.id} className="border-b dark:border-gray-700">
                            <td className="p-2">{vol.name}</td>
                            <td className="p-2">{vol.email}</td>
                            <td className="p-2">{vol.phone}</td>
                            <td className="p-2">{assignedTasks.length} Assigned</td>
                            <td className="p-2">
                                <button 
                                    className="text-sm text-blue-500 hover:underline" 
                                    onClick={() => setModal({ title: `Edit ${vol.name}`, message: `Viewing profile and ${assignedTasks.length} tasks...` })}
                                >
                                    View/Edit
                                </button>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        </div>
    </div>
);

const TaskManagementTab = ({ tasks, setTasks, volunteers, donors, setModal }) => {
    const [desc, setDesc] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [donorId, setDonorId] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!desc.trim() || !assigneeId) return;
        
        const newTask = {
            id: Date.now(),
            text: desc,
            assigneeId: assigneeId,
            donorId: donorId || null,
            completed: false
        };
        setTasks(prev => [newTask, ...prev]);
        setDesc('');
        setAssigneeId('');
        setDonorId('');
    };

    const toggleTask = (id) => {
        setTasks(prev => 
            prev.map(task => 
                task.id === id ? { ...task, completed: !task.completed } : task
            )
        );
    };

    const deleteTask = (id) => {
        setModal({
            title: "Delete Task",
            message: "Are you sure you want to delete this task?",
            confirmText: "Delete",
            onConfirm: () => {
                setTasks(prev => prev.filter(task => task.id !== id));
            }
        });
    };

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">Task Management</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-1">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                        <h4 className="font-semibold mb-3">Create New Task</h4>
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium">Description</label>
                                <textarea 
                                    rows="3" 
                                    className="mt-1 block w-full p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600" 
                                    value={desc}
                                    onChange={(e) => setDesc(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Assign to Volunteer</label>
                                <select 
                                    className="mt-1 block w-full p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600" 
                                    value={assigneeId}
                                    onChange={(e) => setAssigneeId(e.target.value)}
                                    required
                                >
                                    <option value="">Select Volunteer...</option>
                                    {volunteers.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium">Link to Donor (Optional)</label>
                                <select 
                                    className="mt-1 block w-full p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600"
                                    value={donorId}
                                    onChange={(e) => setDonorId(e.target.value)}
                                >
                                    <option value="">None</option>
                                    {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold">Add Task</button>
                        </form>
                    </div>
                </div>
                <div className="md:col-span-2">
                    <h4 className="font-semibold mb-3">Task List</h4>
                    <div className="space-y-3">
                        {tasks.length === 0 && <p className="text-gray-500 dark:text-gray-400">No tasks found.</p>}
                        {tasks.map(task => {
                            const assignee = volunteers.find(v => v.id === task.assigneeId)?.name || 'N/A';
                            const donor = donors.find(d => d.id === task.donorId)?.name || 'N/A';
                            return (
                                <div key={task.id} className={`task-item flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-md ${task.completed ? 'completed' : ''}`}>
                                    <div className="flex items-center gap-3">
                                        <input 
                                            type="checkbox" 
                                            checked={task.completed} 
                                            onChange={() => toggleTask(task.id)} 
                                            className="h-5 w-5 text-blue-500"
                                        />
                                        <div>
                                            <p className="font-medium">{task.text}</p>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                Assigned to: {assignee} | Linked to: {donor}
                                            </span>
                                        </div>
                                    </div>
                                    <button className="text-red-500 hover:text-red-700" onClick={() => deleteTask(task.id)}>
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- UPDATED GrantTrackingTab ---
const GrantTrackingTab = ({ grants, setGrants, setViewingGrantId }) => {
    const [funder, setFunder] = useState('');
    const [amount, setAmount] = useState('');
    const [deadline, setDeadline] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        const newGrant = {
            id: `grt${Date.now()}`,
            funder,
            amount: Number(amount),
            deadline,
            status: 'Drafting',
            description: ''
        };
        setGrants(prev => [newGrant, ...prev]);
        setFunder(''); setAmount(''); setDeadline('');
    };

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">Grant Tracking</h3>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-6">
                <h4 className="font-semibold mb-3">Log New Grant Application</h4>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input type="text" placeholder="Funder Name" value={funder} onChange={e => setFunder(e.target.value)} className="p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600" required />
                        <input type="number" placeholder="Amount ($)" value={amount} onChange={e => setAmount(e.target.value)} className="p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600" required />
                        <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="p-2 border rounded-md bg-white dark:bg-gray-800 dark:border-gray-600" required />
                    </div>
                    <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold">Log Application</button>
                </form>
            </div>
            
            <div className="space-y-3">
                {grants.map(grant => (
                    <div key={grant.id} className="p-4 border dark:border-gray-700 rounded-lg flex justify-between">
                        <div>
                            <p className="font-semibold">{grant.funder}</p>
                            <p className="text-sm text-gray-500">Deadline: {new Date(grant.deadline).toLocaleDateString()}</p>
                            <button 
                                className="text-sm text-blue-500 hover:underline"
                                onClick={() => setViewingGrantId(grant.id)}
                            >
                                View Details
                            </button>
                        </div>
                        <div className="text-right">
                            <p className="font-bold text-lg">${grant.amount.toLocaleString()}</p>
                            <p className="text-sm font-medium text-blue-500">{grant.status}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- NEW GrantDetailsPage ---
const GrantDetailsPage = ({ grant, setGrants, onBack }) => {
    const [status, setStatus] = useState(grant.status);
    const [description, setDescription] = useState(grant.description);

    const handleSave = () => {
        setGrants(prev => prev.map(g => g.id === grant.id ? { ...g, status, description } : g));
        onBack();
    };

    return (
        <div>
            <button onClick={onBack} className="text-sm font-medium text-blue-500 hover:underline mb-4 flex items-center gap-1">
                <ChevronLeft size={16} /> Back to Grants
            </button>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h1 className="text-3xl font-bold mb-2">{grant.funder}</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">Requesting ${grant.amount.toLocaleString()} | Deadline: {new Date(grant.deadline).toLocaleDateString()}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
                    <div>
                        <label className="block text-sm font-medium">Status</label>
                        <select 
                            value={status} 
                            onChange={e => setStatus(e.target.value)} 
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                        >
                            <option>Drafting</option>
                            <option>Submitted</option>
                            <option>Awarded</option>
                            <option>Declined</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium">Description / Notes</label>
                        <textarea 
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows="3" 
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                        />
                    </div>
                </div>
                <button 
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold"
                    onClick={handleSave}
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};


// --- UPDATED WorkflowsTab ---
const WorkflowsTab = ({ workflows, setWorkflows, volunteers, setModal }) => {
    const [trigger, setTrigger] = useState('donation');
    const [condition, setCondition] = useState('gt');
    const [value, setValue] = useState(500);
    const [action, setAction] = useState('task');
    const [taskText, setTaskText] = useState('Send personal thank-you card');
    const [assigneeId, setAssigneeId] = useState(volunteers[0]?.id || '');

    const handleSubmit = (e) => {
        e.preventDefault();
        const newWorkflow = {
            id: Date.now(),
            trigger, condition, value, action, taskText, assigneeId,
            assigneeName: volunteers.find(v => v.id === assigneeId)?.name
        };
        setWorkflows(prev => [...prev, newWorkflow]);
        setModal({ title: "Workflow Created", message: "The new automation rule has been saved." });
    };

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">Automated Workflows</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3">Create New Workflow</h4>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="p-3 border dark:border-gray-600 rounded-lg">
                            <label className="block text-sm font-medium mb-1">Trigger</label>
                            <p>IF a <strong>New Donation</strong> is received...</p>
                        </div>
                        <div className="p-3 border dark:border-gray-600 rounded-lg space-y-2">
                            <label className="block text-sm font-medium mb-1">Condition</label>
                            <div className="flex items-center gap-2">
                                <span>...and the <strong>Amount</strong></span>
                                <select value={condition} onChange={e => setCondition(e.target.value)} className="p-2 rounded-md bg-white dark:bg-gray-800">
                                    <option value="gt">is greater than</option>
                                    <option value="lt">is less than</option>
                                </select>
                                <input type="number" value={value} onChange={e => setValue(e.target.value)} className="w-24 p-2 rounded-md bg-white dark:bg-gray-800" />
                            </div>
                        </div>
                        <div className="p-3 border dark:border-gray-600 rounded-lg space-y-2">
                            <label className="block text-sm font-medium mb-1">Action</label>
                            <p className="mb-2">THEN <strong>Create a Task</strong>:</p>
                            <textarea 
                                value={taskText}
                                onChange={e => setTaskText(e.target.value)}
                                rows="2" 
                                className="w-full p-2 rounded-md bg-white dark:bg-gray-800"
                            />
                            <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} className="w-full p-2 rounded-md bg-white dark:bg-gray-800">
                                <option value="">Assign to...</option>
                                {volunteers.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                            </select>
                        </div>
                        <button type="submit" className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold">Save Workflow</button>
                    </form>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold mb-3">Active Workflows</h4>
                    <div className="space-y-2">
                        {workflows.length === 0 && <p className="text-sm text-gray-500">No workflows created yet.</p>}
                        {workflows.map(wf => (
                            <div key={wf.id} className="p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm">
                                <p><strong>IF</strong> New Donation {wf.condition === 'gt' ? '>' : '<'} ${wf.value}</p>
                                <p><strong>THEN</strong> Create Task: "{wf.taskText}" (Assign to {wf.assigneeName})</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CommunicationsTab = ({ setModal }) => (
    <div>
        <h3 className="text-xl font-semibold mb-4">Mass Communications (Email & SMS)</h3>
        <p className="text-sm text-gray-500 mb-4">Configure your integrations in the 'Integrations' tab.</p>
        <div className="flex gap-4 mb-4">
            <label className="flex items-center">
                <input type="radio" name="comm-type" className="form-radio" defaultChecked />
                <span className="ml-2">Send Mass Email</span>
            </label>
            <label className="flex items-center">
                <input type="radio" name="comm-type" className="form-radio" />
                <span className="ml-2">Send Mass SMS</span>
            </label>
        </div>
        <div className="space-y-4">
            <input type="text" placeholder="Subject Line" className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600" />
            <textarea rows="6" placeholder="Compose your message..." className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600"></textarea>
            <button className="bg-blue-500 text-white px-6 py-2 rounded-lg font-semibold" onClick={() => setModal({ title: "Simulation", message: "Simulating send to all donors via integrated service..." })}>Send Message</button>
        </div>
    </div>
);

// --- UPDATED IntegrationsTab ---
const IntegrationsTab = () => (
    <div>
        <h3 className="text-xl font-semibold mb-4">Integrations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 border dark:border-gray-700 rounded-lg">
                <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-lg flex items-center gap-2"><Smartphone size={20} /> Twilio (SMS)</h4>
                    <span className="text-sm text-green-600">Connected</span>
                </div>
                <p className="text-sm text-gray-500 mt-2 mb-4">Manage settings for mass SMS and Text-to-Donate.</p>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                    <label className="block text-sm font-medium">Text-to-Donate Keyword</label>
                    <div className="flex gap-2 mt-1">
                        <input type="text" value="GIVE" readOnly className="p-2 rounded-md bg-white dark:bg-gray-800 w-1/2" />
                        <span className="p-2 text-gray-500">to</span>
                        <input type="text" value="555-123" readOnly className="p-2 rounded-md bg-white dark:bg-gray-800 w-1/2" />
                    </div>
                </div>
                <button className="bg-gray-200 dark:bg-gray-600 px-4 py-1 rounded-md text-sm font-medium mt-4">Manage</button>
            </div>
            
            <div className="p-4 border dark:border-gray-700 rounded-lg">
                <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-lg flex items-center gap-2"><Mail size={20} /> Mailchimp (Email)</h4>
                    <span className="text-sm text-red-500">Not Connected</span>
                </div>
                <p className="text-sm text-gray-500 mt-2 mb-4">Connect to sync donor lists with Mailchimp for email campaigns.</p>
                <button className="bg-blue-500 text-white px-4 py-1 rounded-md text-sm font-medium mt-10">Connect</button>
            </div>

            <div className="p-4 border dark:border-gray-700 rounded-lg">
                <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-lg flex items-center gap-2"><DollarSign size={20} /> Stripe (Payments)</h4>
                    <span className="text-sm text-green-600">Connected</span>
                </div>
                <p className="text-sm text-gray-500 mt-2 mb-4">Manage connection to Stripe for processing public donations.</p>
                <button className="bg-gray-200 dark:bg-gray-600 px-4 py-1 rounded-md text-sm font-medium mt-10">Manage</button>
            </div>
        </div>
    </div>
);

// --- NEW ReportingPage ---
const ReportingPage = () => (
    <div>
        <h1 className="text-3xl font-bold mb-6">Reporting</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Generate Reports</h3>
                <div className="space-y-3">
                    <button className="w-full text-left bg-gray-50 dark:bg-gray-700 p-4 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex justify-between items-center">
                        <span>Annual Giving Summary (2025)</span>
                        <Download size={18} />
                    </button>
                    <button className="w-full text-left bg-gray-50 dark:bg-gray-700 p-4 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex justify-between items-center">
                        <span>Donations by Campaign Report</span>
                        <Download size={18} />
                    </button>
                    <button className="w-full text-left bg-gray-50 dark:bg-gray-700 p-4 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 flex justify-between items-center">
                        <span>Volunteer Activity Report</span>
                        <Download size={18} />
                    </button>
                </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4">Donor Retention Rate</h3>
                <img src="https://placehold.co/600x250/e0e7ff/3b82f6?text=Donor+Retention+Chart" alt="Donor Retention Chart" className="w-full rounded-md"/>
            </div>
        </div>
    </div>
);


// --- DONOR PLATFORM ("MyGiving") ---

const MyGivingPlatformPage = ({ user, receipts, setReceipts, setModal }) => {
    const [donorTab, setDonorTab] = useState('dashboard');

    useEffect(() => {
        api.getMyDonations()
            .then(({ donations }) => {
                const apiReceipts = donations.map(d => ({
                    id: d.id,
                    org: d.nonprofit_name || 'General Donation',
                    amount: d.total,
                    date: d.timestamp,
                    type: 'System',
                    receiptNumber: d.receipt_number,
                }));
                setReceipts(prev => {
                    const uploaded = (prev[user.id] || []).filter(r => r.type === 'Uploaded');
                    return { ...prev, [user.id]: [...apiReceipts, ...uploaded] };
                });
            })
            .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    return (
        <div>
            <div className="flex items-center gap-4 mb-8">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg">
                    <User size={32} className="text-white" />
                </div>
                <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Donor Dashboard</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your philanthropy and impact</p>
                </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="flex border-b dark:border-gray-700 overflow-x-auto">
                    <TabButton name="dashboard" label="My Dashboard" activeTab={donorTab} setTab={setDonorTab} />
                    <TabButton name="receipts" label="My Tax Receipts" activeTab={donorTab} setTab={setDonorTab} />
                    <TabButton name="gifting" label="Gifting Tools" activeTab={donorTab} setTab={setDonorTab} />
                </div>
                
                <div className="p-6">
                    {donorTab === 'dashboard' && <MyGivingDashboard user={user} receipts={receipts} />}
                    {donorTab === 'receipts' && <MyTaxReceipts user={user} receipts={receipts} setReceipts={setReceipts} setModal={setModal} />}
                    {donorTab === 'gifting' && <MyGiftingTools />}
                </div>
            </div>
        </div>
    );
};

const MyGivingDashboard = ({ user, receipts }) => {
    const totalGiven = useMemo(() => {
        return receipts[user.id]?.reduce((acc, r) => acc + r.amount, 0) || 0;
    }, [user, receipts]);
    const orgs = useMemo(() => {
        return new Set(receipts[user.id]?.map(r => r.org) || []).size;
    }, [user, receipts]);
    
    return (
        <div className="space-y-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-2">Welcome back, {user.name}!</h2>
                    <p className="text-blue-100 opacity-90 max-w-md">Thank you for your incredible support. Your contributions make a real difference in the community.</p>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-20 -mt-20"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-400 opacity-20 rounded-full -ml-10 -mb-10"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard title="Total Given" value={`$${totalGiven.toLocaleString()}`} icon={<DollarSign size={28} />} />
                <StatCard title="Orgs Supported" value={orgs} icon={<Users size={28} />} />
                <StatCard title="Gifts This Year" value={(receipts[user.id] || []).length} icon={<Target size={28} />} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Recent Giving</h3>
                        <button className="text-sm font-semibold text-blue-500 hover:text-blue-600 transition-colors">See All</button>
                    </div>
                    <div className="space-y-4">
                        {(receipts[user.id] || []).slice(0, 4).map(r => (
                            <div key={r.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:scale-[1.02] transition-transform cursor-pointer">
                                <div className="flex items-center gap-4">
                                    <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                                        <HandCoins className="text-purple-500" size={20} />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900 dark:text-white">{r.org}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(r.date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <p className="font-bold text-lg text-blue-600 dark:text-blue-400">+${r.amount.toLocaleString()}</p>
                            </div>
                        ))}
                        {(!receipts[user.id] || receipts[user.id].length === 0) && (
                            <p className="text-center text-gray-500 py-8 italic">No recent donations found.</p>
                        )}
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
                    <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Giving Activity</h3>
                    <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={(receipts[user.id] || []).map(r => ({ date: r.date, amount: r.amount })).reverse()}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                <XAxis dataKey="date" hide />
                                <YAxis hide />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                    formatter={(value) => [`$${value}`, 'Amount']}
                                />
                                <Line type="monotone" dataKey="amount" stroke="#8b5cf6" strokeWidth={4} dot={{ r: 6, fill: '#8b5cf6' }} activeDot={{ r: 8 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center gap-4">
                        <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg text-blue-600 dark:text-blue-300">
                            <Zap size={20} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">Impact Insights</p>
                            <p className="text-xs text-blue-700 dark:text-blue-300">You've supported projects that impacted over 500 lives this year!</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

function DonorReceiptCard({ r, setModal }) {
    const [sending, setSending] = useState(false);

    const handleResend = async () => {
        setSending(true);
        try {
            await api.sendReceipt(r.id);
            setModal({ title: 'Receipt Sent', message: 'Your tax receipt has been re-sent to your email address.' });
        } catch (err) {
            setModal({ title: 'Error', message: err.message || 'Failed to send receipt. Please try again.' });
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="p-4 border dark:border-gray-700 rounded-lg flex justify-between items-center hover:shadow-md transition-shadow">
            <div>
                <div className="flex items-center gap-2">
                    <p className="font-semibold">{r.org}</p>
                    {r.autoExtracted && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">Auto-Detected</span>
                    )}
                    {r.type === 'System' && (
                        <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-1 rounded">Verified</span>
                    )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(r.date).toLocaleDateString()}
                    {r.type === 'Uploaded' && ` • ${r.fileName}`}
                </p>
                {r.receiptNumber && (
                    <p className="text-xs font-mono text-blue-500 dark:text-blue-400">#{r.receiptNumber}</p>
                )}
            </div>
            <div className="flex items-center gap-3">
                {r.type === 'System' && (
                    <button
                        onClick={handleResend}
                        disabled={sending}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                    >
                        <Mail size={12} />
                        {sending ? 'Sending…' : 'Resend'}
                    </button>
                )}
                <p className="font-bold text-xl text-green-600 dark:text-green-400">${Number(r.amount).toLocaleString()}</p>
            </div>
        </div>
    );
}

function DonorReceiptList({ receipts, setModal }) {
    if (receipts.length === 0) {
        return <p className="text-sm text-gray-400 text-center py-8">No receipts yet. Your donation receipts will appear here.</p>;
    }
    return (
        <div className="space-y-3">
            {receipts.map(r => <DonorReceiptCard key={r.id} r={r} setModal={setModal} />)}
        </div>
    );
}

const MyTaxReceipts = ({ user, receipts, setReceipts, setModal }) => {
    const fileInputRef = React.useRef(null);
    const [showExtractedData, setShowExtractedData] = useState(false);
    const [extractedInfo, setExtractedInfo] = useState(null);

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    // PDF text extraction and auto-detection
    const extractReceiptInfo = async (file) => {
        // PRODUCTION IMPLEMENTATION OPTIONS:
        //
        // Option 1: Client-side PDF parsing with pdf.js
        // npm install pdfjs-dist
        // import * as pdfjsLib from 'pdfjs-dist';
        // const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
        // Extract text and parse with regex patterns for:
        // - Amount: /\$?\d+[,.]?\d*\.?\d{2}/ or /total.*\$(\d+\.?\d*)/i
        // - Date: /\d{1,2}\/\d{1,2}\/\d{4}/ or /date.*(\d{1,2}\/\d{1,2}\/\d{4})/i
        // - Org Name: First line or header text
        // - Receipt #: /receipt.*#?(\w+)/i or /reference.*#?(\w+)/i
        //
        // Option 2: Backend API with OCR (Tesseract.js, AWS Textract, Google Vision API)
        // const formData = new FormData();
        // formData.append('file', file);
        // const response = await fetch('/api/extract-receipt', { method: 'POST', body: formData });
        // const extractedData = await response.json();
        //
        // Option 3: AI-powered extraction (OpenAI GPT-4 Vision, Claude with PDFs)
        // Send PDF to AI model with prompt: "Extract donation amount, date, organization name, and receipt number"

        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                // DEMO MODE: Simulating extraction with mock data
                // In production, replace this with actual PDF parsing
                const mockExtractedData = {
                    amount: Math.floor(Math.random() * 900) + 100,
                    date: new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString(),
                    org: ['United Way', 'Red Cross', 'Local Food Bank', 'Community Foundation', 'Animal Shelter'][Math.floor(Math.random() * 5)],
                    donorName: user.name,
                    receiptNumber: `RCP-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
                    confidence: 'High'
                };

                resolve(mockExtractedData);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            setModal({ title: "Invalid File", message: "Please upload a PDF file." });
            return;
        }

        // Show processing message
        setModal({ title: "Processing...", message: "Extracting information from PDF receipt..." });

        try {
            // Extract information from PDF
            const extractedData = await extractReceiptInfo(file);

            setExtractedInfo({
                ...extractedData,
                fileName: file.name,
                file: file
            });

            setShowExtractedData(true);
            setModal(null);
        } catch (error) {
            setModal({ title: "Error", message: "Failed to process PDF. You can still upload it manually." });
        }
    };

    const handleConfirmExtraction = () => {
        const newReceipt = {
            id: `upload-${Date.now()}`,
            org: extractedInfo.org,
            amount: extractedInfo.amount,
            date: extractedInfo.date,
            type: 'Uploaded',
            fileName: extractedInfo.fileName,
            receiptNumber: extractedInfo.receiptNumber,
            autoExtracted: true
        };

        setReceipts(prev => ({
            ...prev,
            [user.id]: [newReceipt, ...(prev[user.id] || [])]
        }));

        setShowExtractedData(false);
        setExtractedInfo(null);
        setModal({ title: "Upload Successful", message: `Receipt from ${extractedInfo.org} has been added with auto-detected information.` });
    };

    const handleEditExtraction = (field, value) => {
        setExtractedInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <div>
            <h3 className="text-xl font-semibold mb-4">My Tax Receipts</h3>
            <button
                onClick={handleUploadClick}
                className="mb-4 bg-green-500 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-green-600"
            >
                <FileUp size={18} /> Upload PDF Receipt (Auto-Detect)
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="application/pdf" />

            {/* Extracted Data Modal */}
            {showExtractedData && extractedInfo && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                                Auto-Detected Receipt Information
                            </h3>
                            <button onClick={() => setShowExtractedData(false)} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6 flex items-start gap-3">
                            <AlertCircle size={24} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-blue-900 dark:text-blue-100">Information Extracted from PDF</p>
                                <p className="text-sm text-blue-700 dark:text-blue-300">Review and edit the auto-detected fields below before saving. Confidence: {extractedInfo.confidence}</p>
                            </div>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-semibold mb-2">Organization Name</label>
                                <input
                                    type="text"
                                    value={extractedInfo.org}
                                    onChange={(e) => handleEditExtraction('org', e.target.value)}
                                    className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Donation Amount ($)</label>
                                    <input
                                        type="number"
                                        value={extractedInfo.amount}
                                        onChange={(e) => handleEditExtraction('amount', parseFloat(e.target.value) || 0)}
                                        className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Date</label>
                                    <input
                                        type="date"
                                        value={extractedInfo.date.split('T')[0]}
                                        onChange={(e) => handleEditExtraction('date', new Date(e.target.value).toISOString())}
                                        className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold mb-2">Donor Name</label>
                                    <input
                                        type="text"
                                        value={extractedInfo.donorName}
                                        onChange={(e) => handleEditExtraction('donorName', e.target.value)}
                                        className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2">Receipt Number</label>
                                    <input
                                        type="text"
                                        value={extractedInfo.receiptNumber}
                                        onChange={(e) => handleEditExtraction('receiptNumber', e.target.value)}
                                        className="w-full p-3 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    <strong>File:</strong> {extractedInfo.fileName}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleConfirmExtraction}
                                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                            >
                                <Check size={20} /> Confirm & Save Receipt
                            </button>
                            <button
                                onClick={() => setShowExtractedData(false)}
                                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <DonorReceiptList receipts={receipts[user.id] || []} setModal={setModal} />
        </div>
    );
};

const MyGiftingTools = () => (
    <div>
        <h3 className="text-xl font-semibold mb-4">My Gifting Tools</h3>
        <h4 className="font-semibold mt-6 mb-2">Employer Gift Matching</h4>
        <input type="text" placeholder="Search your employer..." className="p-2 border rounded-md bg-gray-50 dark:bg-gray-700 dark:border-gray-600 w-full" />
        <h4 className="font-semibold mt-6 mb-2">Giving Basket</h4>
        <p>A list of organizations on the platform you can add to a basket for a single, one-time checkout.</p>
    </div>
);

// --- NEW Volunteer Portal ---
const VolunteerPortalPage = ({ user, tasks, setTasks, volunteers, donors }) => {
    const [page, setPage] = useState('tasks');
    const myTasks = useMemo(() => tasks.filter(t => t.assigneeId === user.id), [tasks, user.id]);

    const toggleTask = (id) => {
        setTasks(prev => 
            prev.map(task => 
                task.id === id ? { ...task, completed: !task.completed } : task
            )
        );
    };
    
    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Volunteer Portal</h1>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
                <div className="flex border-b dark:border-gray-700 overflow-x-auto">
                    <TabButton name="tasks" label="My Tasks" activeTab={page} setTab={setPage} />
                    <TabButton name="impact" label="My Impact" activeTab={page} setTab={setPage} />
                </div>
                <div className="p-6">
                    {page === 'tasks' && (
                        <div>
                            <h3 className="text-xl font-semibold mb-4">My Assigned Tasks</h3>
                            <div className="space-y-3">
                                {myTasks.length === 0 && <p className="text-gray-500 dark:text-gray-400">You have no pending tasks. Great job!</p>}
                                {myTasks.map(task => {
                                    const donor = donors.find(d => d.id === task.donorId)?.name || 'N/A';
                                    return (
                                        <div key={task.id} className={`task-item flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-800 rounded-md ${task.completed ? 'completed' : ''}`}>
                                            <div className="flex items-center gap-3">
                                                <input 
                                                    type="checkbox" 
                                                    checked={task.completed} 
                                                    onChange={() => toggleTask(task.id)} 
                                                    className="h-5 w-5 text-blue-500"
                                                />
                                                <div>
                                                    <p className="font-medium">{task.text}</p>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        Linked to: {donor}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {page === 'impact' && (
                        <div>
                            <h3 className="text-xl font-semibold mb-4">My Impact</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <StatCard title="Tasks Completed" value={myTasks.filter(t => t.completed).length} icon={<Check size={28} />} />
                                <StatCard title="Donations Influenced" value="$1,350" icon={<DollarSign size={28} />} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};


// --- Main App Component ---

export default function App() {
    const { user: currentUser, logout } = useAuth();

    // --- State ---
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
    const [modalConfig, setModalConfig] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [page, setPage] = useState('dashboard');
    const [viewingEventId, setViewingEventId] = useState(null);
    const [viewingGrantId, setViewingGrantId] = useState(null);

    // Data state (non-donation data still managed in-component for CRM features)
    const [donors, setDonors] = useState(MOCK_DONORS);
    const [campaigns, setCampaigns] = useState(MOCK_CAMPAIGNS);
    const [events, setEvents] = useState(MOCK_EVENTS);
    const [pledges, setPledges] = useState(MOCK_PLEDGES);
    const [tasks, setTasks] = useState(MOCK_TASKS);
    const [grants, setGrants] = useState(MOCK_GRANTS);
    const [users, setUsers] = useState(Object.values(MOCK_USERS_DB));
    const [volunteers, setVolunteers] = useState(MOCK_VOLUNTEERS);
    const [notes, setNotes] = useState(MOCK_NOTES);
    const [receipts, setReceipts] = useState(MOCK_RECEIPTS);
    const [workflows, setWorkflows] = useState([]);

    // --- Effects ---
    useEffect(() => {
        const html = document.documentElement;
        html.classList.toggle('dark', theme === 'dark');
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        if (currentUser?.role === 'admin') {
            api.getDonors()
                .then(({ donors: apiDonors }) => {
                    if (apiDonors.length > 0) {
                        setDonors(apiDonors.map(d => ({ ...d, totalDonated: d.totalDonated ?? 0, giftCount: d.giftCount ?? 0 })));
                    }
                    // If DB is empty, keep the example donors as placeholders
                })
                .catch(() => {});
            api.getUsers()
                .then(({ users: apiUsers }) => {
                    if (apiUsers.length > 0) {
                        setUsers(apiUsers);
                    }
                    // If DB is empty, keep the example users as placeholders
                })
                .catch(() => {});
        }
    }, [currentUser]);

    const handleLogout = async () => {
        await logout();
    };

    // --- Workflow Logic ---
    const checkWorkflows = (donation) => {
        workflows.forEach(wf => {
            let conditionMet = false;
            if (wf.trigger === 'donation') {
                if (wf.condition === 'gt' && donation.amount > wf.value) {
                    conditionMet = true;
                } else if (wf.condition === 'lt' && donation.amount < wf.value) {
                    conditionMet = true;
                }
            }
            
            if (conditionMet && wf.action === 'task') {
                const newTask = {
                    id: Date.now(),
                    text: wf.taskText,
                    assigneeId: wf.assigneeId,
                    donorId: null, // Could link this if donation form collects donor
                    completed: false
                };
                setTasks(prev => [newTask, ...prev]);
                // Show modal after a short delay so it doesn't collide with the donation modal
                setTimeout(() => {
                    setModalConfig({ title: "Workflow Triggered!", message: `A new task was created: "${wf.taskText}"`});
                }, 100);
            }
        });
    };

    // --- Page Navigation ---
    const adminPages = {
        'dashboard': { title: 'Dashboard', icon: <LayoutDashboard size={20} /> },
        'donations': { title: 'Donations', icon: <List size={20} /> },
        'reports': { title: 'Reporting', icon: <BarChartHorizontal size={20} /> },
        'donors': { title: 'Donors', icon: <Users size={20} /> },
        'campaigns': { title: 'Campaigns', icon: <Target size={20} /> },
        'events': { title: 'Events', icon: <Calendar size={20} /> },
        'pledges': { title: 'Pledges', icon: <HandCoins size={20} /> },
        'manual-donation': { title: 'Add Donation', icon: <PlusSquare size={20} /> },
        'donate-public': { title: 'Donate Form', icon: <Send size={20} /> },
        'admin': { title: 'Admin Portal', icon: <Settings size={20} /> },
    };

    const donorPages = {
        'mygiving': { title: 'Donor Dashboard', icon: <User size={20} /> },
        'donate-public': { title: 'Find a Non-Profit', icon: <Send size={20} /> },
    };
    
    const volunteerPages = {
        'tasks': { title: 'My Tasks', icon: <Check size={20} /> },
        'impact': { title: 'My Impact', icon: <BarChart2 size={20} /> },
    };

    const role = currentUser.role?.toLowerCase();
    const pages = role === 'admin' ? adminPages : (role === 'volunteer' ? volunteerPages : donorPages);

    const renderPage = () => {
        // --- Special Case: Event Details ---
        if (viewingEventId) {
            const event = events.find(e => e.id === viewingEventId);
            return <EventDetailsPage event={event} setModal={setModalConfig} onBack={() => setViewingEventId(null)} />;
        }
        // --- Special Case: Grant Details ---
        if (viewingGrantId) {
            const grant = grants.find(g => g.id === viewingGrantId);
            return <GrantDetailsPage grant={grant} setGrants={setGrants} onBack={() => setViewingGrantId(null)} />;
        }

        // --- User Role Views ---
        if (role === 'donor') {
            switch (page) {
                case 'mygiving': return <MyGivingPlatformPage user={currentUser} receipts={receipts} setReceipts={setReceipts} setModal={setModalConfig} />;
                case 'donate-public': return <PublicDonationPage setModal={setModalConfig} checkWorkflows={checkWorkflows} />;
                default: setPage('mygiving'); return <MyGivingPlatformPage user={currentUser} receipts={receipts} setReceipts={setReceipts} setModal={setModalConfig} />;
            }
        }
        
        if (role === 'volunteer') {
            switch (page) {
                case 'tasks': return <VolunteerPortalPage user={currentUser} tasks={tasks} setTasks={setTasks} volunteers={volunteers} donors={donors} />;
                case 'impact': return <VolunteerPortalPage user={currentUser} tasks={tasks} setTasks={setTasks} volunteers={volunteers} donors={donors} />; // This component handles its own tabs
                default: setPage('tasks'); return <VolunteerPortalPage user={currentUser} tasks={tasks} setTasks={setTasks} volunteers={volunteers} donors={donors} />;
            }
        }
        
        // Admin View
        switch (page) {
            case 'dashboard': return <DashboardPage donors={donors} tasks={tasks} campaigns={campaigns} />;
            case 'donations': return <AdminDonationsPage />;
            case 'reports': return <ReportingPage />;
            case 'donors': return <DonorManagementPage donors={donors} setDonors={setDonors} notes={notes} setNotes={setNotes} receipts={receipts} pledges={pledges} setModal={setModalConfig} />;
            case 'campaigns': return <CampaignManagementPage campaigns={campaigns} setCampaigns={setCampaigns} setModal={setModalConfig} />;
            case 'events': return <EventManagementPage events={events} setEvents={setEvents} setViewingEventId={setViewingEventId} />;
            case 'pledges': return <PledgeManagementPage pledges={pledges} setPledges={setPledges} donors={donors} setDonors={setDonors} setModal={setModalConfig} />;
            case 'manual-donation': return <ManualDonationPage donors={donors} setDonors={setDonors} campaigns={campaigns} setDonations={setDonors} setModal={setModalConfig} checkWorkflows={checkWorkflows} setReceipts={setReceipts} />;
            case 'donate-public': return <PublicDonationPage setModal={setModalConfig} checkWorkflows={checkWorkflows} />;
            case 'admin': return <AdminPortalPage users={users} setUsers={setUsers} tasks={tasks} setTasks={setTasks} grants={grants} setGrants={setGrants} volunteers={volunteers} donors={donors} setModal={setModalConfig} workflows={workflows} setWorkflows={setWorkflows} setViewingGrantId={setViewingGrantId} />;
            default: return <DashboardPage donors={donors} tasks={tasks} campaigns={campaigns} />;
        }
    };
    
    return (
        <div className={`app-root flex font-sans ${theme} text-slate-900 dark:text-slate-200`}>
            <ConfirmationModal config={modalConfig} onClose={() => setModalConfig(null)} />

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)}></div>
            )}

            {/* Sidebar */}
            <aside className={`fixed lg:static inset-y-0 left-0 w-64 bg-gradient-to-b from-white to-gray-50 dark:from-slate-900 dark:to-slate-950 shadow-2xl p-4 flex-shrink-0 flex flex-col justify-between border-r border-gray-200 dark:border-slate-800 z-50 transition-transform duration-300 lg:translate-x-0 overflow-y-auto ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div>
                    <div className="flex items-center gap-2 px-3 mb-8 mt-2">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                            <Heart size={24} className="text-white" />
                        </div>
                        <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            DonorDash
                        </span>
                    </div>
                    <nav className="space-y-2">
                        {Object.entries(pages).map(([pageId, { title, icon }]) => (
                            <a
                                key={pageId}
                                href="#"
                                className={`sidebar-link ${page === pageId ? 'active' : ''}`}
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    setPage(pageId); 
                                    setViewingEventId(null); 
                                    setViewingGrantId(null);
                                    setIsSidebarOpen(false);
                                }}
                            >
                                {icon}
                                {title}
                            </a>
                        ))}
                    </nav>
                </div>
                <div>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-900 border border-gray-200 dark:border-slate-700 shadow-md">
                        <div className="relative">
                            <div className="w-10 h-10 rounded-full ring-2 ring-blue-500 ring-offset-2 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                {currentUser?.name?.charAt(0) || '?'}
                            </div>
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-700"></div>
                        </div>
                        <div className="dark:text-white overflow-hidden">
                            <span className="font-semibold block truncate">{currentUser?.name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium capitalize">{currentUser?.role}</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-4 px-2">
                        <button
                            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                            className="p-2.5 rounded-xl hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-gray-600 dark:text-gray-300 transition-all shadow-sm hover:shadow-md"
                            title="Toggle theme"
                        >
                            {theme === 'light' ? <Moon size={20} className="text-blue-600" /> : <Sun size={20} className="text-yellow-400" />}
                        </button>
                        <button
                            className="p-2.5 rounded-xl hover:bg-gradient-to-br hover:from-red-50 hover:to-orange-50 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all shadow-sm hover:shadow-md"
                            onClick={handleLogout}
                            title="Sign Out"
                        >
                            <LogOut size={20} className="text-red-600 dark:text-red-400" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-slate-950 dark:via-slate-950 dark:to-slate-900 overflow-y-auto relative">
                {/* Mobile Header */}
                <div className="lg:hidden p-4 flex items-center justify-between bg-white dark:bg-slate-900 border-b dark:border-slate-800 sticky top-0 z-30">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg shadow">
                            <Heart size={18} className="text-white" />
                        </div>
                        <span className="font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">DonorDash</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            {theme === 'light' ? <Moon size={20} className="text-blue-600" /> : <Sun size={20} className="text-yellow-400" />}
                        </button>
                        <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
                <div className="p-4 lg:p-8 mobile-main-content">
                    {renderPage()}
                </div>
            </main>

            {/* Bottom Navigation Bar (mobile only) */}
            <nav className="bottom-nav">
                {role === 'admin' && (
                    <>
                        <button className={`bottom-nav-item ${page === 'dashboard' ? 'active' : ''}`} onClick={() => { setPage('dashboard'); setViewingEventId(null); setViewingGrantId(null); }}>
                            <span className="bottom-nav-icon"><LayoutDashboard size={22} /></span>
                            <span>Dashboard</span>
                        </button>
                        <button className={`bottom-nav-item ${page === 'donors' ? 'active' : ''}`} onClick={() => { setPage('donors'); setViewingEventId(null); setViewingGrantId(null); }}>
                            <span className="bottom-nav-icon"><Users size={22} /></span>
                            <span>Donors</span>
                        </button>
                        <button className={`bottom-nav-item ${page === 'donations' ? 'active' : ''}`} onClick={() => { setPage('donations'); setViewingEventId(null); setViewingGrantId(null); }}>
                            <span className="bottom-nav-icon"><List size={22} /></span>
                            <span>Donations</span>
                        </button>
                        <button className={`bottom-nav-item ${page === 'campaigns' ? 'active' : ''}`} onClick={() => { setPage('campaigns'); setViewingEventId(null); setViewingGrantId(null); }}>
                            <span className="bottom-nav-icon"><Target size={22} /></span>
                            <span>Campaigns</span>
                        </button>
                        <button className={`bottom-nav-item`} onClick={() => setIsSidebarOpen(true)}>
                            <span className="bottom-nav-icon"><Menu size={22} /></span>
                            <span>More</span>
                        </button>
                    </>
                )}
                {role === 'donor' && (
                    <>
                        <button className={`bottom-nav-item ${page === 'mygiving' ? 'active' : ''}`} onClick={() => setPage('mygiving')}>
                            <span className="bottom-nav-icon"><User size={22} /></span>
                            <span>My Giving</span>
                        </button>
                        <button className={`bottom-nav-item ${page === 'donate-public' ? 'active' : ''}`} onClick={() => setPage('donate-public')}>
                            <span className="bottom-nav-icon"><Send size={22} /></span>
                            <span>Donate</span>
                        </button>
                    </>
                )}
                {role === 'volunteer' && (
                    <>
                        <button className={`bottom-nav-item ${page === 'tasks' ? 'active' : ''}`} onClick={() => setPage('tasks')}>
                            <span className="bottom-nav-icon"><Check size={22} /></span>
                            <span>Tasks</span>
                        </button>
                        <button className={`bottom-nav-item ${page === 'impact' ? 'active' : ''}`} onClick={() => setPage('impact')}>
                            <span className="bottom-nav-icon"><BarChart2 size={22} /></span>
                            <span>Impact</span>
                        </button>
                    </>
                )}
            </nav>
        </div>
    );
}